import { Injectable } from "@angular/core";
import * as Highcharts from "highcharts";
import more from "highcharts/highcharts-more";
import HighchartsExport from "highcharts/modules/exporting";
import HCExportData from "highcharts/modules/export-data";
import { NzMessageService } from "ng-zorro-antd/message";
import noData from "highcharts/modules/no-data-to-display";
import { Dictionary } from "../store/models/common_types";
import { Subject } from "rxjs";
HighchartsExport(Highcharts);
HCExportData(Highcharts);
more(Highcharts);
noData(Highcharts);

@Injectable({
  providedIn: "root",
})
export class ChartPlotterService {
  showTitle = false;
  globalHighchartOptions: Highcharts.Options;
  chartTypeDisplay = "";
  addinExportEvent = new Subject<Dictionary<any>>();
  addinExportEvent$ = this.addinExportEvent.asObservable();
  seriesInfo;
  pageName = "";
  constructor(private message: NzMessageService) {
    this.globalHighchartOptions = {
      time: {
        useUTC: false,
      },
      lang: {
        thousandsSep: ",",
        loading: "Crunching Numbers...",
      },
      loading: {
        labelStyle: {
          color: "black",
          fontWeight: "900",
          opacity: 1,
        },
        style: {
          backgroundColor: "white",
        },
      },
      chart: {
        style: {
          fontFamily:
            "Open sans, Lucida Grande , Segoe UI , Arial, Helvetica, Verdana, sans-serif",
        },
        zoomType: "x",
        panning: {
          enabled: true,
          type: "xy",
        },
        panKey: "shift",
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        series: {
          events: {
            legendItemClick: function () {
              const series = this.yAxis.series,
                seriesLen = series.length;
              let visible = this.visible ? 0 : 1;
              // 0 when visible series, because it will be changed after that callback
              for (let i = 0; i < seriesLen; i++) {
                if (series[i].visible) {
                  visible++;
                }
              }
              if (visible === 1) {
                return false;
              }
              return true;
            },
          },
          marker: {
            radius: 3,
            states: {
              hover: {
                radius: 3,
                lineColor: "black",
              },
            },
          },
        },
      },
      yAxis: {
        gridLineColor: "#ECF0F1",
        labels: {
          format: "{value:.2f}",
        },
      },
      title: {
        text: "&nbsp;&nbsp;",
        useHTML: true,
      },
      exporting: {
        showTable: false,
        enabled: false,
        tableCaption: " ",
        scale: 1.2,
        buttons: {
          contextButton: {
            menuItems: ["downloadPNG", "downloadPDF"],
          },
        },
        csv: {
          dateFormat: "%d-%b-%Y",
        },
      },
    };
    window.align_zero_axis(Highcharts);
  }
  getZoneIndexForEstimatedPeriods(
    xAxisPeriodArray,
    lineTickerSpecificArray = [],
  ) {
    let estimatedItem = null;
    for (const item of lineTickerSpecificArray) {
      if (item.includes("E")) {
        estimatedItem = item;
        break;
      }
    }
    if (estimatedItem && xAxisPeriodArray) {
      for (let i = 0; i < xAxisPeriodArray.length; i++) {
        if (estimatedItem === xAxisPeriodArray[i]) {
          return i;
        }
      }
    }
    return undefined;
  }

  formatRevisionData(revisionData, earningsDate?: string): Array<any> {
    if (!earningsDate) {
      earningsDate = "";
    }
    const formatedData = [];
    for (let i = 0; i < revisionData.length; i++) {
      const date = revisionData[i][0].split("-");
      // checks if current datapoint is disconnected from adjacent points i.e. no neighbours
      // if it disconnected we are enabling marker for such points
      const isSingleMarkPointer =
        revisionData[i] &&
        revisionData[i][1] !== undefined &&
        ((!revisionData[i - 1] && !revisionData[i + 1]) ||
          (!revisionData[i - 1] &&
            revisionData[i + 1] &&
            revisionData[i + 1][1] === undefined) ||
          (!revisionData[i + 1] &&
            revisionData[i - 1] &&
            revisionData[i - 1][1] === undefined) ||
          (revisionData[i - 1] &&
            revisionData[i - 1][1] === undefined &&
            revisionData[i + 1] &&
            revisionData[i + 1][1] === undefined));
      if (earningsDate.includes(revisionData[i][0])) {
        formatedData.push({
          x: new Date(
            Number(date[0]),
            Number(date[1]) - 1,
            Number(date[2]),
          ).getTime(),
          y: revisionData[i][1],
          marker: {
            enabled: true,
            fillColor: "#E59700 ",
            lineWidth: 2,
            lineColor: "transparent",
          },
        });
      } else if (
        !isNaN(date[0]) &&
        !earningsDate.includes(revisionData[i][0])
      ) {
        formatedData.push({
          x: new Date(
            Number(date[0]),
            Number(date[1]) - 1,
            Number(date[2]),
          ).getTime(),
          y: revisionData[i][1],
          marker: {
            enabled: isSingleMarkPointer,
            radius: 2,
          },
        });
      } else {
        formatedData.push({
          y: revisionData[i][1],
          marker: {
            enabled: isSingleMarkPointer,
            radius: 2,
          },
        });
      }
    }
    return formatedData;
  }
  getMultiCompanyBarChartOptions(dataSet) {
    const categories = [];
    const yAxisData = [];
    for (let i = 0; i < dataSet.data.length; i++) {
      categories.push(dataSet.data[i].x);
      yAxisData.push(dataSet.data[i].y);
    }
    dataSet["categories"] = categories;
    dataSet["plotData"] = yAxisData;
    const multiCompanyBarOptions: Highcharts.Options = {
      chart: {
        type: "column",
        zoomType: "xy",
      },
      title: {
        text: this.showTitle
          ? `<span title="${dataSet.y}">${dataSet.y}</span>`
          : "",
        useHTML: true,
        align: "left",
        y: 25,
      },
      xAxis: {
        categories: dataSet.categories,
      },
      yAxis: {
        title: {
          text: `${dataSet.y} (In ${dataSet.yc}${dataSet.ym})`,
        },
        labels: {
          format: `${dataSet.yc}{value:.2f}${
            dataSet.yu !== "Amount" ? dataSet.yu : ""
          }`,
        },
      },
      tooltip: {
        enabled: true,
        useHTML: true,
        formatter: function () {
          let toolTipHTML = "<div>";
          toolTipHTML +=
            `<div><span><b>${this.x}: </b></span><span>${
              dataSet.data[this.point.index].full_name
            }</span></div>` +
            `<div><span><b>${dataSet.y}: </b></span><span>${
              dataSet.yc
            }${Highcharts.numberFormat(this.point.y, 2, ".", ",")} ${
              dataSet.yu !== "Amount" ? dataSet.yu : ""
            }</span></div>`;
          toolTipHTML += "</div>";
          return toolTipHTML;
        },
      },
      legend: {
        enabled: true,
      },
      plotOptions: {
        series: {
          dataLabels: {
            enabled: true,
            format: "{point.name}",
          },
        },
      },
      series: [
        {
          data: dataSet.plotData,
          name: "Tickers",
          type: "column",
        },
      ],
    };
    return multiCompanyBarOptions;
  }
  commonRevisionOptions(seriesObj, dataSet, stockPriceData?, minInterval?) {
    const formatterFunction = this.formatCell;
    const chartOptions: Highcharts.Options = {
      chart: {
        type: "line",
        zoomType: "xy",
        panning: {
          enabled: true,
          type: "xy",
        },
        panKey: "shift",
      },
      title: {
        text: "&nbsp;&nbsp;",
        useHTML: true,
      },
      xAxis: {
        type: "datetime",
        dateTimeLabelFormats: {
          month: "%e %b %Y",
          year: "%e %b %Y",
          day: "%e %b %Y",
        },
        minRange: 7 * 24 * 3600 * 1000,
        minTickInterval: minInterval,
        title: {
          text: null,
        },
      },

      yAxis: [
        {
          title: {
            text: null,
          },
        },
        stockPriceData
          ? {
              gridLineWidth: 1,
              title: {
                text: null,
              },
              labels: {
                style: {
                  fontSize: "14px",
                  minWidth: "100px",
                  maxWidth: "160px",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                },
                formatter: function () {
                  const formattedValue = formatterFunction(
                    this.value,
                    stockPriceData.currency,
                    "Amount",
                    "",
                  );
                  return formattedValue;
                },
              },
              opposite: true,
            }
          : {
              title: {
                text: null,
              },
            },
      ],

      tooltip: {
        headerFormat: "",
        formatter: function () {
          const xVal = Number(this.x);
          const xDate = Highcharts.dateFormat("%d-%b-%Y", xVal);
          let label = "";

          if (stockPriceData && this.series.name === "PRICE") {
            label += `Date: ${xDate}<br/> Price: ${
              stockPriceData.currency || ""
            }${this.y}`;
            return label;
          }

          if (
            dataSet.earnings_date &&
            dataSet.earnings_date.indexOf(
              Highcharts.dateFormat("%Y-%m-%d", xVal),
            ) !== -1
          ) {
            if (
              dataSet.earnings_date_w_rev &&
              dataSet.earnings_date_w_rev.indexOf(
                Highcharts.dateFormat("%Y-%m-%d", xVal),
              ) !== -1
            ) {
              label = `Earnings date: ${xDate}<br/>`;
            } else {
              return `<div class="highchartTooltip">
              Earnings Date: ${xDate}</div>`;
            }
          }
          let isAmount = false;
          if (dataSet.unit === "Amount") {
            isAmount = true;
          }
          const consensus =
            isAmount === true
              ? dataSet.display_unit + this.y
              : this.y + dataSet.display_unit;

          if (dataSet.percentChange) {
            label += `<b>% Change </b><br/>${this.series.name} Value: ${consensus} <br/>Revision date: ${xDate}`;
            return label;
          }
          if (this.series.name.toLowerCase() === "consensus") {
            const tooltip = dataSet.broker_count_list[this.point.index];
            label += `Revision date: ${xDate}<br/>${
              this.series.name
            } value: ${consensus}<br/>No. of brokers: ${
              tooltip === null ? "NA" : tooltip
            }`;
            return label;
          }

          label += `Revision date: ${xDate}<br/>${this.series.name} value: ${consensus}`;

          return label;
        },
      },
      legend: {
        enabled: true,
        // useHTML: true,
        // symbolHeight: 0,
        // labelFormatter: () => {
        //   if(legendCount === 0){
        //     legendCount++;
        //     return ` <div class="data-point-legends">
        //     <span class="earnings-color"></span><span>Earnings</span>
        //     <span class="revisions-color"></span><span>Revision Dates</span>
        //     </div>`;
        //   }
        // }
      },
      plotOptions: {
        line: {
          step: "left",
        },
      },
      series: seriesObj,
    };
    let exportOptions: Highcharts.ExportingOptions = {};
    this.seriesInfo = dataSet;
    if (dataSet.export) {
      exportOptions = this.getExportSettings(dataSet, dataSet.chartType);
      chartOptions["exporting"] = exportOptions;
    }
    return chartOptions;
  }

  formatMonitoringRevisionData(revisionData, filingsPeriod) {
    const formatedData = [];
    for (let i = 0; i < revisionData.length; i++) {
      const date = revisionData[i][0].split("-");
      if (filingsPeriod === revisionData[i][0]) {
        formatedData.push({
          x: Date.UTC(Number(date[0]), Number(date[1]) - 1, Number(date[2])),
          y: revisionData[i][1],
          marker: {
            fillColor: "#E59700 ",
            lineWidth: 2,
            lineColor: "transparent",
          },
          showTooltip: true,
        });
      } else {
        formatedData.push({
          x: Date.UTC(Number(date[0]), Number(date[1]) - 1, Number(date[2])),
          y: revisionData[i][1],
          marker: { enabled: false, states: { hover: { enabled: false } } },
          showTooltip: false,
        });
      }
    }
    return formatedData;
  }

  getMiniDistributionDataOptions(dataSet) {
    let minVal = dataSet.min;
    const maxVal = dataSet.max;
    let diff = 0;
    let temp = 0;
    const bucketList = [];
    const id = dataSet.distId;
    const sourceVal = dataSet.source_values;
    const currentvalue = dataSet.currentvalue;
    const intermediateChartData = [];
    let linePlacementPos = -1;

    if (maxVal < 0 && minVal < 0) {
      //Case when both the min and max values are negative
      diff = Math.abs((Math.abs(maxVal) - Math.abs(minVal)) / 5);
    } else {
      //Case when min and max are both positive or when min is negative and max is positive
      diff = (Math.abs(maxVal) - minVal) / 5;
    }

    for (let i = 0; i < 5; i++) {
      const chartsData = { min: minVal, name: `${id}_${i}` };
      let count = 0;
      if (i === 4) {
        temp = Math.round(maxVal * 1000) / 1000;
      } else {
        temp = Math.round((minVal + diff - 0.001) * 1000) / 1000;
      }
      minVal = Math.round((temp + 0.001) * 1000) / 1000;

      chartsData["max"] = temp;
      intermediateChartData.push(chartsData);

      if (
        linePlacementPos === -1 &&
        currentvalue >= chartsData["min"] &&
        currentvalue <= chartsData["max"]
      ) {
        linePlacementPos = i;
      }
      if (diff === 0) {
        //Case when both min and max values are the same
        count = 0;
        linePlacementPos = 2;

        if (i === 2) {
          count = sourceVal.length;
        }
      } else if (sourceVal) {
        sourceVal.forEach((val) => {
          if (val >= chartsData["min"] && val <= chartsData["max"]) {
            count++;
          }
        });
      }

      //Converting 0 values to null and added minPointLength to 2 so that small values are also visible
      bucketList.push({ y: count === 0 ? null : count });
    }

    const distributionOptions: Highcharts.Options = {
      chart: {
        type: "column",
        margin: [5, 5, 5, 5],
        borderWidth: 0,
        animation: false,
        backgroundColor: "rgba(255, 255, 255, 0.002)",
      },

      credits: { enabled: false },

      title: { text: "" },
      tooltip: { enabled: false },
      legend: { enabled: false },
      plotOptions: {
        series: {
          enableMouseTracking: false,
          shadow: false,
          marker: {
            enabled: false,
          },
          borderWidth: 0,
          lineWidth: 2,
          animation: false,
          connectNulls: true,
        },
      },
      xAxis: {
        lineColor: "#AFAFB4",
        title: { text: "" },
        lineWidth: 1,
        tickWidth: 0,
        gridZIndex: 2,
        labels: {
          enabled: false,
        },
        plotLines: [
          {
            color: "#808080",
            value: linePlacementPos,
            width: 1,
            zIndex: 10,
          },
        ],
      },
      yAxis: {
        title: { text: "" },
        lineWidth: 0,
        tickWidth: 0,
        gridLineWidth: 0,
        labels: {
          enabled: false,
        },
      },

      series: [
        {
          data: bucketList,
          name: "CONCENSUS",
          type: "column",
        },
      ],
    };
    return distributionOptions;
  }

  validateActuals(quarterVal, annualVal, currentComparison) {
    if (
      (quarterVal !== "FY" &&
        (parseInt(currentComparison.split("-")[1]) > parseInt(annualVal) ||
          (parseInt(currentComparison.split("-")[1]) === parseInt(annualVal) &&
            parseInt(quarterVal.charAt(0)) <
              parseInt(currentComparison.split("-")[0].charAt(0))))) ||
      (quarterVal === "FY" &&
        parseInt(currentComparison.split("-")[1]) > parseInt(annualVal))
    ) {
      return true;
    }

    return false;
  }

  getMiniTrendDataOptions(dataSet) {
    const chartFinal = [];
    const chartData = dataSet.Value.data.normal;
    chartData.forEach((value, key) => {
      const quarter = value[0].split("-")[0];
      const year = value[0].split("-")[1].replace(/[^0-9]/g, "");

      if (value[0].indexOf(dataSet.period) !== -1) {
        chartFinal[key] = { y: value[1], color: "#6BAA6F" };
      } else {
        if (
          this.validateActuals(quarter, year, dataSet.firstEstimatedPeriod) ===
          true
        ) {
          chartFinal[key] = { y: value[1], color: "#B2B2B2" };
        } else {
          chartFinal[key] = { y: value[1], color: "#6CB6F1" };
        }
      }
    });

    const trendBarOptions: Highcharts.Options = {
      chart: {
        type: "column",
        margin: [5, 5, 5, 5],
        borderWidth: 0,
        animation: false,
        backgroundColor: "rgba(255, 255, 255, 0.002)",
      },
      credits: { enabled: false },
      exporting: { enabled: false },
      title: { text: "" },
      tooltip: { enabled: false },
      legend: { enabled: false },
      xAxis: [
        {
          categories: dataSet.Value.x,
        },
      ],
      yAxis: [
        {
          // Primary yAxis
          labels: {
            enabled: false,
          },
          title: { text: "" },
          lineWidth: 0,
          tickWidth: 0,
          gridLineWidth: 0,
        },
      ],
      plotOptions: {
        series: {
          enableMouseTracking: false,
          shadow: false,
          marker: {
            enabled: false,
          },
          borderWidth: 0,
          animation: false,
        },
      },
      series: [
        {
          name: "",
          type: "column",
          data: chartFinal,
        },
      ],
    };
    return trendBarOptions;
  }

  formatCell(data, currency, unit, valueType: string) {
    const sliCompsData = data;

    const valueToUse = new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(sliCompsData);
    let unitType = unit;
    if (
      !currency ||
      valueType === "yoy" ||
      valueType === "revision" ||
      valueType === "index"
    ) {
      currency = "";
    }
    if (unitType === "%" && valueType !== "index") {
      unitType = valueType === "yoy" || valueType === "revision" ? "bps" : "%";
    } else {
      unitType = valueType === "yoy" || valueType === "revision" ? "%" : "";
    }

    return `${currency}${valueToUse}${unitType}`;
  }

  formateDate(type, range) {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    const currentMonth = d.getMonth();
    let year = d.getFullYear();
    if (type === "min") {
      let resultMonth = currentMonth - range;
      if (resultMonth < 0) {
        year -= 1;
        resultMonth = 12 + resultMonth;
      }
      return Date.UTC(year, resultMonth);
    }

    return Date.UTC(year, currentMonth);
  }

  getYAxisOptions(isSecondaryAxis: boolean, meta: any) {
    const formatterFunction = this.formatCell;
    if (!isSecondaryAxis) {
      return [
        {
          title: {
            text: `${
              meta.lineInfo[meta.lines[0]].formatted_name !== undefined
                ? meta.lineInfo[meta.lines[0]].formatted_name
                : `${meta.lineInfo[meta.lines[0]].name} ${
                    meta.lineInfo[meta.lines[0]].magnitude &&
                    meta.lineInfo[meta.lines[0]].magnitude !== "" &&
                    meta.lineInfo[meta.lines[0]].magnitude !== "Abs" &&
                    meta.scaleType === 1
                      ? `(in ${meta.lineInfo[meta.lines[0]].magnitude})`
                      : ""
                  }`
            }<br>
            ${
              meta.lineInfo[meta.lines[1]]
                ? meta.lineInfo[meta.lines[1]].formatted_name !== undefined
                  ? meta.lineInfo[meta.lines[1]].formatted_name
                  : `${meta.lineInfo[meta.lines[1]].name} ${
                      meta.lineInfo[meta.lines[1]].magnitude &&
                      meta.lineInfo[meta.lines[1]].magnitude !== "" &&
                      meta.lineInfo[meta.lines[1]].magnitude !== "Abs" &&
                      meta.scaleType === 1
                        ? `(in ${meta.lineInfo[meta.lines[1]].magnitude})`
                        : ""
                    }`
                : ""
            }`,
            style: {
              fontSize: "14px",
              minWidth: "100px",
              maxWidth: "160px",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            },
          },
          // max: meta.scaleType !== 1 ? 100 : null,
          labels: {
            formatter: function () {
              const formattedValue = formatterFunction(
                this.value,
                meta.lineInfo[meta.lines[0]].currSymbol ||
                  meta.lineInfo[meta.lines[0]].curr_symbol,
                meta.lineInfo[meta.lines[0]].unit,
                meta.scaleType === 2 ? "index" : meta.dataView,
              );
              return formattedValue;
            },
          },
        },
      ];
    }

    return [
      {
        title: {
          text: `${
            meta.lineInfo[meta.lines[0]].formatted_name !== undefined
              ? meta.lineInfo[meta.lines[0]].formatted_name
              : `${meta.lineInfo[meta.lines[0]].name} ${
                  meta.lineInfo[meta.lines[0]].magnitude &&
                  meta.lineInfo[meta.lines[0]].magnitude !== "" &&
                  meta.lineInfo[meta.lines[0]].magnitude !== "Abs" &&
                  meta.scaleType === 1
                    ? `(in ${meta.lineInfo[meta.lines[0]].magnitude})`
                    : ""
                }`
          }`,
          style: {
            fontSize: "14px",
            minWidth: "100px",
            maxWidth: "160px",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          },
        },
        // max: meta.scaleType !== 1 ? 100 : null,
        labels: {
          formatter: function () {
            const formattedValue = formatterFunction(
              this.value,
              meta.lineInfo[meta.lines[0]].currSymbol ||
                meta.lineInfo[meta.lines[0]].curr_symbol,
              meta.lineInfo[meta.lines[0]].unit,
              meta.scaleType === 2 ? "index" : meta.dataView,
            );
            return formattedValue;
          },
        },
      },
      {
        title: {
          text: `
              ${
                meta.lineInfo[meta.lines[1]]
                  ? meta.lineInfo[meta.lines[1]].formatted_name !== undefined
                    ? meta.lineInfo[meta.lines[1]].formatted_name
                    : `${meta.lineInfo[meta.lines[1]].name} ${
                        meta.lineInfo[meta.lines[1]].magnitude &&
                        meta.lineInfo[meta.lines[1]].magnitude !== "" &&
                        meta.lineInfo[meta.lines[1]].magnitude !== "Abs" &&
                        meta.scaleType === 1
                          ? `(in ${meta.lineInfo[meta.lines[1]].magnitude})`
                          : ""
                      }`
                  : ""
              }`,
          style: {
            fontSize: "14px",
            minWidth: "100px",
            maxWidth: "160px",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          },
        },
        // max: meta.scaleType !== 1 ? 100 : null,
        opposite: true,
        labels: {
          formatter: function () {
            const formattedValue = formatterFunction(
              this.value,
              meta.lineInfo[meta.lines[1]].currSymbol ||
                meta.lineInfo[meta.lines[1]].curr_symbol,
              meta.lineInfo[meta.lines[1]].unit,
              meta.scaleType === 2 ? "index" : meta.dataView,
            );
            return formattedValue;
          },
        },
      },
    ];
  }

  getIndustryPageTimeseriesOptions(meta) {
    const seriesData = [];
    const dataObj = {};
    meta.scaleType === 1
      ? Object.assign(dataObj, meta.absVal)
      : Object.assign(dataObj, meta.indexVal);
    meta.lines.forEach((l) => {
      for (let i = 0; i < meta.tickers.length; i++) {
        const t = meta.tickers[i];
        let hasNoData = true;
        const objData =
          dataObj[l][meta.dataView][t] &&
          dataObj[l][meta.dataView][t].map((d, index) => {
            if (d) {
              hasNoData = false;
            }
            return {
              y: d,
              opacity: 1,
              name: `${l}-${t}`,
              isSecondaryAxis: meta.isSecondaryAxis,
              lineIndex: index,
              type: meta.typeVal[l][index],
            };
          });

        if (objData) {
          const zoneIndex = this.getZoneIndexForEstimatedPeriods(
            meta["x"],
            meta.respectivePeriodData[l][t],
          );
          const obj = {
            type: "line",
            name: t,
            // color: meta.legendColors[i + 6 * lineIndex],
            dv: meta.dataView,
            scale: meta.scaleType === 1 ? "abs" : "index",
            dashStyle: "solid",
            marker: {
              enabled: true,
              radius: 4,
            },
            custom: {
              parentLine: l,
              hasNoData: hasNoData,
              tickerInfo: meta.selectedTickersInfo
                ? meta.selectedTickersInfo[i]
                : undefined,
            },
            // yAxis: i,
            visible: hasNoData ? false : true,
            data: objData,
            zoneAxis: "x",
            zones: [
              {
                value: zoneIndex,
              },
              {
                dashStyle: "dash",
              },
            ],
          };
          obj["yAxis"] = 0;
          seriesData.push(obj);
        }
      }
    });

    const formatterFunction = this.formatCell;
    const alertMessage = this.message;
    const isTotalOrAvg =
      meta.selectedTickersInfo &&
      meta.selectedTickersInfo.length &&
      (meta.selectedTickersInfo[0].ticker === "Total" ||
        meta.selectedTickersInfo[0].ticker === "Average");
    const chartOptions: Highcharts.Options = {
      title: {
        text: "",
      },

      subtitle: {
        text: "",
      },

      yAxis: this.getYAxisOptions(meta.isSecondaryAxis, meta),
      xAxis: {
        categories: meta["x"],
        min: 0,
      },

      tooltip: {
        headerFormat: "",
        backgroundColor: "rgba(248, 248,248,1)",
        useHTML: true,
        outside: true,
        formatter: function () {
          let tooltipHtml = "";
          const ticker = this.point.series.name.startsWith("Total")
            ? "Total"
            : this.point.series.name.startsWith("Average")
              ? "Average"
              : this.point.series.name;
          const year = this.point["category"].toString().includes("E")
            ? this.point["category"]
                .toString()
                .substring(0, this.point["category"].toString().length - 1)
            : this.point["category"];
          const type = meta.typeVal[this.key.toString().split("-")[0]][
            this.series["userOptions"].name
          ][meta.x.indexOf(this.x)]
            ? meta.typeVal[this.key.toString().split("-")[0]][
                this.series["userOptions"].name
              ][meta.x.indexOf(this.x)]
            : "";
          const lineId = parseInt(this.key.toString().split("-")[0], 10);
          const lineData = meta.lineInfo[lineId];
          const currSymbol = lineData
            ? lineData.currSymbol || lineData.curr_symbol
            : null;
          const unit = lineData ? lineData.unit : null;
          const magnitude = lineData
            ? lineData.magnitude !== "Abs" &&
              this.point.series["userOptions"]["dv"] !== "yoy"
              ? lineData.magnitude
              : ""
            : null;

          const indexVal =
            this.point.series["userOptions"]["scale"] !== "index"
              ? ""
              : `Indexed Value: ${meta.indexVal[lineId][meta.dataView][
                  this.point.series.name
                ][this.point.index].toFixed(2)}`;
          const formattedValue = formatterFunction(
            meta.absVal[this.key.toString().split("-")[0]][
              this.point.series["userOptions"]["dv"]
            ][this.key.toString().substr(this.key.toString().indexOf("-") + 1)][
              this.point.index
            ],
            currSymbol,
            unit,
            this.point.series["userOptions"]["scale"] === "index"
              ? this.point.series["userOptions"]["scale"]
              : this.point.series["userOptions"]["dv"],
          );
          // const displayYear = year
          //   .toString()
          //   .split("-")[0]
          //   .includes("Q")
          //   ? year
          //   : year.toString().split("-")[1];
          tooltipHtml += `${ticker} ${year} - ${type} <br/>${lineData.name}: ${formattedValue}${magnitude}<br/>
           ${indexVal}`;

          return tooltipHtml;
        },
      },

      legend: {
        title: {
          text: "",
        },
        align: "left",
        layout: "vertical",
        itemMarginBottom: 4,
        verticalAlign: "top",
        margin: 28,
        itemDistance: 10,
        width: isTotalOrAvg ? 190 : undefined,
        itemStyle: {
          textOverflow: "ellipsis",
        },
        useHTML: true,
        labelFormatter: function () {
          const { custom } = (this as any).userOptions;
          const hoverTitle =
            custom &&
            custom.tickerInfo &&
            custom.tickerInfo.name &&
            custom.tickerInfo.ticker &&
            custom.tickerInfo.ticker !== "Total" &&
            custom.tickerInfo.ticker !== "Average"
              ? `${custom.tickerInfo.ticker} - ${custom.tickerInfo.name}`
              : this.name;
          return `<span title="${hoverTitle}">${this.name}</span>`;
        },
      },
      plotOptions: {
        series: {
          events: {
            legendItemClick: function () {
              if (this["userOptions"].custom.isLineItem === true) {
                const lineId = this["userOptions"].custom.lineId;
                const shouldHide = this.visible;

                Highcharts.each(this.chart.series, function (s) {
                  if (
                    s["userOptions"].custom &&
                    s["userOptions"].custom.parentLine &&
                    s["userOptions"].custom.parentLine === lineId
                  ) {
                    if (!s["userOptions"].custom.hasNoData) {
                      if (shouldHide) {
                        s.setVisible(false, false);
                      } else {
                        s.setVisible(true, false);
                      }
                    }
                  }
                });
                this.chart.redraw();
              } else {
                if (this["userOptions"].custom.hasNoData === true) {
                  alertMessage.warning("No data available.");
                  return false;
                }
              }
              return true;
            },
          },
        },
      },
      colors: meta.legendColors,
      series: seriesData,
    };
    return chartOptions;
  }

  getIndustryPageRevisionOptions(meta, data) {
    const seriesData = [];
    const line = meta.lines[0];
    const period = meta.periods[0];
    for (let i = 0; i < meta.tickers.length; i++) {
      let dataObj = [];
      const t = meta.tickers[i];
      const index = i;
      if (
        data[index].data[t] &&
        data[index].data[t][line] &&
        data[index].data[t][line][period]
      ) {
        dataObj = data[index].data[t][line][period]["values"].map(
          (v, valueIndex) => {
            return {
              x: Date.parse(v[0]),
              y: v[meta.scaleType],
              abs: v[1],
              ind: v[2],
              ticker: t,
              period: period,
              isEarningsDate: false,
              brokerCount:
                data[index].data[t][line][period]["broker_count_list"][
                  valueIndex
                ],
              marker: {
                //Enable marker if current datapoint has no left neighbour and right neighbour
                enabled:
                  (data[index].data[t][line][period]["values"].length === 1 &&
                    data[index].data[t][line][period]["values"][valueIndex]
                      .length > 1) ||
                  (!data[index].data[t][line][period]["values"][
                    valueIndex - 1
                  ] &&
                    data[index].data[t][line][period]["values"][
                      valueIndex + 1
                    ] &&
                    data[index].data[t][line][period]["values"][valueIndex + 1]
                      .length === 1) ||
                  (!data[index].data[t][line][period]["values"][
                    valueIndex + 1
                  ] &&
                    data[index].data[t][line][period]["values"][
                      valueIndex - 1
                    ] &&
                    data[index].data[t][line][period]["values"][valueIndex - 1]
                      .length === 1) ||
                  (data[index].data[t][line][period]["values"][
                    valueIndex - 1
                  ] &&
                    data[index].data[t][line][period]["values"][valueIndex - 1]
                      .length === 1 &&
                    data[index].data[t][line][period]["values"][
                      valueIndex + 1
                    ] &&
                    data[index].data[t][line][period]["values"][valueIndex + 1]
                      .length === 1),
                radius: 2,
              },
            };
          },
        );
      }

      const seriesName = `${t}`;
      const selectedTickersInfo = meta.selectedTickersInfo
        ? { ...meta.selectedTickersInfo[i], period: period }
        : undefined;
      const obj = {
        type: "line",
        name: seriesName,
        marker: {
          enabled: false,
        },
        custom: {
          parentLine: line,
          tickerInfo: selectedTickersInfo,
        },
        scale: meta.scaleType === 1 ? "abs" : "index",
        visible: dataObj.length > 0 ? true : false,
        isSecondaryAxis: false,
        data: dataObj,
      };
      seriesData.push(obj);
    }

    const formatterFunction = this.formatCell;
    const dateFormatter = this.formateDate;
    const alertMessage = this.message;

    const chartOptions: Highcharts.Options = {
      title: {
        text: "",
      },

      subtitle: {
        text: "",
      },

      yAxis: this.getYAxisOptions(false, meta),
      xAxis: {
        type: "datetime",
        min: meta.revision.startsWith("custom")
          ? Date.UTC(
              parseInt(meta.revision.split("|")[1].split("-")[2], 10),
              parseInt(meta.revision.split("|")[1].split("-")[0], 10) - 2,
            )
          : dateFormatter("min", parseInt(meta.revision.split("m")[0], 10) + 1),
        max: dateFormatter("max", 6),
        tickInterval: 365 * 24 * 3600 * 60,
        labels: {
          format: "{value:%b %Y}",
        },
      },

      tooltip: {
        headerFormat: "",
        backgroundColor: "rgba(248, 248,248,1)",
        useHTML: true,
        outside: true,
        formatter: function () {
          const xDate = Highcharts.dateFormat("%d-%b-%Y", Number(this.x));
          let tooltipHtml = `<div>${this.point["ticker"]} ${this.point["period"]}<br>`;
          const indexVal =
            this.point.series["userOptions"]["scale"] !== "index"
              ? ""
              : `Indexed Value: ${this.point["ind"]}<br>`;
          const formattedValue = formatterFunction(
            this.point["abs"],
            meta.lineInfo[line].currSymbol || meta.lineInfo[line].curr_symbol,
            meta.lineInfo[line].unit,
            "values",
          );
          tooltipHtml += `${meta.lineInfo[line].name}: ${formattedValue}${
            meta.lineInfo[line].magnitude !== "Abs"
              ? meta.lineInfo[line].magnitude
              : ""
          }<br>`;
          tooltipHtml += `${indexVal}`;

          if (this.point["isEarningsDate"] === true) {
            tooltipHtml += `Earnings Date: ${xDate}<br>`;
            if (this.point["isEarningsDateWithRev"] === true) {
              const brokerCount = this.point["brokerCount"];
              tooltipHtml += `Revision date: ${xDate}<br># of brokers: ${
                brokerCount === null ? "NA" : brokerCount
              }`;
            }
          } else {
            const brokerCount = this.point["brokerCount"];
            tooltipHtml += `Revision date: ${xDate}<br># of brokers: ${
              brokerCount === null ? "NA" : brokerCount
            }`;
          }
          tooltipHtml += "</div>";
          return tooltipHtml;
        },
      },

      legend: {
        title: {
          text: "",
        },
        align: "left",
        layout: "vertical",
        itemMarginBottom: 4,
        verticalAlign: "top",
        itemDistance: 10,
        width: undefined,
        itemStyle: {
          textOverflow: "ellipsis",
        },
        margin: 28,
        useHTML: true,
        labelFormatter: function () {
          const { custom } = (this as any).userOptions;
          const hoverTitle =
            custom &&
            custom.tickerInfo &&
            custom.tickerInfo.name &&
            custom.tickerInfo.ticker &&
            (custom.tickerInfo.ticker !== "Total" ||
              custom.tickerInfo.ticker !== "Average")
              ? `${custom.tickerInfo.ticker} - ${custom.tickerInfo.name} ${
                  custom.tickerInfo.period
                    ? `- ${custom.tickerInfo.period}`
                    : ""
                }`
              : this.name;
          return `<span title="${hoverTitle}">${this.name}</span>`;
        },
      },

      plotOptions: {
        line: {
          step: "left",
        },
        series: {
          events: {
            legendItemClick: function () {
              if (this.data.length === 0) {
                alertMessage.warning("No data available.");
                return false;
              }
              return true;
            },
          },
        },
      },
      series: seriesData,
    };
    return chartOptions;
  }

  getIndustryPageTotalAvgRevisionOptions(meta, data, chartType) {
    const seriesData = [];
    const line = meta.lines[0];
    const period = meta.periods[0];
    let dataObj = [];
    const t =
      chartType === "industry-page-total-revision"
        ? `Total - ${meta.lineInfo[line].name}`
        : `Average - ${meta.lineInfo[line].name}`;
    dataObj = data["ind_rev_data"].map((v, valueIndex) => {
      const year = v.year_month.split("-")[0];
      const month = v.year_month.split("-")[1];
      const lastDay = new Date(year, month, 0);
      const isAbsoluteValueSelected = meta.scaleType === 1;
      return {
        x: lastDay,
        y: isAbsoluteValueSelected
          ? chartType === "industry-page-total-revision"
            ? v.value
            : v.value / data.count
          : v.index,
        abs:
          chartType === "industry-page-total-revision"
            ? v.value
            : v.value / data.count,
        ind: v.index,
        ticker: t,
        period: period,
        isEarningsDate: false,
        marker: {
          //Enable marker if current datapoint has no left neighbour and right neighbour
          enabled:
            (!v["value"][valueIndex - 1] &&
              v["value"][valueIndex + 1] &&
              v["value"][valueIndex + 1].length === 1) ||
            (!v["value"][valueIndex + 1] &&
              v["value"][valueIndex - 1] &&
              v["value"][valueIndex - 1].length === 1) ||
            (v["value"][valueIndex - 1] &&
              v["value"][valueIndex - 1].length === 1 &&
              v["value"][valueIndex + 1] &&
              v["value"][valueIndex + 1].length === 1),
          radius: 2,
        },
      };
    });

    const seriesName = `${t}`;
    if (dataObj && dataObj.length) {
      const obj = {
        type: "line",
        name: seriesName,
        marker: {
          enabled: false,
        },
        custom: {
          parentLine: line,
        },
        scale: meta.scaleType === 1 ? "abs" : "index",
        visible: dataObj.length > 0 ? true : false,
        isSecondaryAxis: false,
        data: dataObj,
      };
      seriesData.push(obj);
    }

    const formatterFunction = this.formatCell;
    const dateFormatter = this.formateDate;
    const alertMessage = this.message;

    const chartOptions: Highcharts.Options = {
      title: {
        text: "",
      },

      subtitle: {
        text: "",
      },

      yAxis: this.getYAxisOptions(false, meta),
      xAxis: {
        type: "datetime",
        min: meta.revision.startsWith("custom")
          ? Date.UTC(
              parseInt(meta.revision.split("|")[1].split("-")[2], 10),
              parseInt(meta.revision.split("|")[1].split("-")[0], 10) - 2,
            )
          : dateFormatter("min", parseInt(meta.revision.split("m")[0], 10) + 1),
        max: dateFormatter("max", 6),
        tickInterval: 365 * 24 * 3600 * 60,
        labels: {
          format: "{value:%b %Y}",
        },
      },

      tooltip: {
        headerFormat: "",
        backgroundColor: "rgba(248, 248,248,1)",
        useHTML: true,
        outside: true,
        formatter: function () {
          let tooltipHtml = `<div>${
            this.point["ticker"].startsWith("Total")
              ? "Total"
              : this.point["ticker"].startsWith("Average")
                ? "Average"
                : this.point["ticker"]
          } ${this.point["period"]}<br>`;
          const indexVal =
            this.point.series["userOptions"]["scale"] !== "index"
              ? ""
              : `Indexed Value: ${this.point["ind"]}<br>`;
          const formattedValue = formatterFunction(
            this.point["abs"],
            meta.lineInfo[line].currSymbol || meta.lineInfo[line].curr_symbol,
            meta.lineInfo[line].unit,
            "values",
          );
          tooltipHtml += `${meta.lineInfo[line].name}: ${formattedValue}${
            meta.lineInfo[line].magnitude !== "Abs"
              ? meta.lineInfo[line].magnitude
              : ""
          }<br>`;
          tooltipHtml += `${indexVal}`;
          tooltipHtml += "</div>";
          return tooltipHtml;
        },
      },

      legend: {
        title: {
          text: "",
        },
        align: "left",
        layout: "vertical",
        itemMarginBottom: 4,
        verticalAlign: "top",
        margin: 28,
        itemDistance: 10,
        width: 190,
        itemStyle: {
          textOutline: "ellipsis",
        },
        useHTML: true,
        labelFormatter: function () {
          const { custom } = (this as any).userOptions;
          const hoverTitle =
            custom &&
            custom.tickerInfo &&
            custom.tickerInfo.name &&
            custom.tickerInfo.ticker &&
            (custom.tickerInfo.ticker !== "Total" ||
              custom.tickerInfo.ticker !== "Average")
              ? `${custom.tickerInfo.ticker} - ${custom.tickerInfo.name} ${
                  custom.tickerInfo.period
                    ? `- ${custom.tickerInfo.period}`
                    : ""
                }`
              : this.name;
          return `<span title="${hoverTitle}">${this.name}</span>`;
        },
      },

      plotOptions: {
        line: {
          step: "left",
        },
        series: {
          events: {
            legendItemClick: function () {
              if (this.data.length === 0) {
                alertMessage.warning("No data available.");
                return false;
              }
              return true;
            },
          },
        },
      },
      series: seriesData,
    };
    return chartOptions;
  }

  getChartsPageRevisionOptions(meta, data) {
    const lineType = {
      "0": "Dot",
      "1": "ShortDot",
      "2": "ShortDashDot",
      "3": "Dash",
      "4": "LongDash",
      "5": "LongDashDot",
      "6": "LongDashDotDot",
    };

    const thisYear = new Date().getFullYear();
    let yearWithSolid =
      meta.otherPeriods && meta.otherPeriods.length
        ? meta.otherPeriods[0]
        : meta.periods[0];
    let dashStyleIndex = 0;
    const chartPeriods =
      meta.otherPeriods && meta.otherPeriods.length
        ? meta.otherPeriods
        : meta.periods;

    if (meta.otherPeriods && meta.otherPeriods.length === 0) {
      let diff = Math.abs(parseInt(yearWithSolid.slice(-4)) - thisYear);
      meta.periods.forEach((p) => {
        const newYear = p;
        const newDiff = Math.abs(parseInt(newYear.slice(-4)) - thisYear);
        if (newDiff < diff) {
          yearWithSolid = newYear;
          diff = newDiff;
        }
      });
    }

    const seriesData = [];
    const isComparativePageChart = meta.isComparativePageChart;
    if (isComparativePageChart) {
      meta.lines.forEach((l, lineIndex) => {
        chartPeriods.forEach((p) => {
          dashStyleIndex = dashStyleIndex === 6 ? 0 : dashStyleIndex + 1;
          for (let i = 0; i < meta.tickers.length; i++) {
            const dataIndex = i + meta.tickers.length * lineIndex;
            let dataObj = [];
            const t = meta.tickers[i];
            if (
              t === "Average" &&
              data[dataIndex] &&
              data[dataIndex].ind_rev_data
            ) {
              dataObj = data[dataIndex].ind_rev_data.map((v, valueIndex) => {
                const year = v.year_month.split("-")[0];
                const month = v.year_month.split("-")[1];
                const lastDay = new Date(year, month, 0);
                const isAbsoluteValueSelected = meta.scaleType === 1;
                return {
                  x: lastDay.getTime(),
                  y: isAbsoluteValueSelected
                    ? v.value / data[dataIndex].count
                    : v.index,
                  abs: v.value / data[dataIndex].count,
                  ind: v.index,
                  ticker: t,
                  period: p,
                  isEarningsDate: false,
                  marker: {
                    //Enable marker if current datapoint has no left neighbour and right neighbour
                    enabled:
                      (!v["value"][valueIndex - 1] &&
                        v["value"][valueIndex + 1] &&
                        v["value"][valueIndex + 1].length === 1) ||
                      (!v["value"][valueIndex + 1] &&
                        v["value"][valueIndex - 1] &&
                        v["value"][valueIndex - 1].length === 1) ||
                      (v["value"][valueIndex - 1] &&
                        v["value"][valueIndex - 1].length === 1 &&
                        v["value"][valueIndex + 1] &&
                        v["value"][valueIndex + 1].length === 1),
                    radius: 2,
                  },
                };
              });
            } else if (
              data[dataIndex] &&
              data[dataIndex].data[t] &&
              data[dataIndex].data[t][l] &&
              data[dataIndex].data[t][l][p]
            ) {
              const earningsDate = data[dataIndex].data[t][l]["earnings_date"];
              const earningsDateWithRev =
                data[dataIndex].data[t][l]["earnings_date_w_rev"];
              dataObj = data[dataIndex].data[t][l][p]["values"].map(
                (v, valueIndex) => {
                  const date = v[0].split("-");
                  if (earningsDate && earningsDate.indexOf(v[0]) !== -1) {
                    return {
                      x: new Date(
                        Number(date[0]),
                        Number(date[1]) - 1,
                        Number(date[2]),
                      ).getTime(),
                      y: v[meta.scaleType],
                      abs: v[1],
                      ind: v[2],
                      marker: {
                        enabled: true,
                        fillColor: "#fc762b",
                        symbol: "Circle",
                      },
                      ticker: t,
                      period: p,
                      isEarningsDate: true,
                      isEarningsDateWithRev:
                        earningsDateWithRev &&
                        earningsDateWithRev.indexOf(v[0]) !== -1,
                      lineIndex: lineIndex,
                      brokerCount:
                        data[dataIndex].data[t][l][p]["broker_count_list"][
                          valueIndex
                        ],
                    };
                  }
                  return {
                    x: new Date(
                      Number(date[0]),
                      Number(date[1]) - 1,
                      Number(date[2]),
                    ).getTime(),
                    y: v[meta.scaleType],
                    abs: v[1],
                    ind: v[2],
                    marker: {
                      //Enable marker if current datapoint has no left neighbour and right neighbour
                      enabled:
                        (!data[dataIndex].data[t][l][p]["values"][
                          valueIndex - 1
                        ] &&
                          data[dataIndex].data[t][l][p]["values"][
                            valueIndex + 1
                          ] &&
                          data[dataIndex].data[t][l][p]["values"][
                            valueIndex + 1
                          ].length === 1) ||
                        (!data[dataIndex].data[t][l][p]["values"][
                          valueIndex + 1
                        ] &&
                          data[dataIndex].data[t][l][p]["values"][
                            valueIndex - 1
                          ] &&
                          data[dataIndex].data[t][l][p]["values"][
                            valueIndex - 1
                          ].length === 1) ||
                        (data[dataIndex].data[t][l][p]["values"][
                          valueIndex - 1
                        ] &&
                          data[dataIndex].data[t][l][p]["values"][
                            valueIndex - 1
                          ].length === 1 &&
                          data[dataIndex].data[t][l][p]["values"][
                            valueIndex + 1
                          ] &&
                          data[dataIndex].data[t][l][p]["values"][
                            valueIndex + 1
                          ].length === 1),
                      radius: 2,
                    },
                    ticker: t,
                    period: p,
                    isEarningsDate: false,
                    lineIndex: lineIndex,
                    brokerCount:
                      data[dataIndex].data[t][l][p]["broker_count_list"][
                        valueIndex
                      ],
                  };
                },
              );
            }

            dataObj = dataObj.sort((a, b) => a.x - b.x);
            const seriesName = `${t}-${p}`;
            const selectedTickersInfo = meta.selectedTickersInfo
              ? { ...meta.selectedTickersInfo[i], period: p }
              : undefined;
            const obj = {
              type: meta.lineInfo[l].isValuationRatio ? "spline" : "line",
              name: seriesName,
              dashStyle:
                p === yearWithSolid ? "solid" : lineType[dashStyleIndex],
              color: meta.legendColors[i + 6 * lineIndex],
              marker: {
                enabled: dataObj.length === 1 ? true : false,
                radius: 4,
              },
              custom: {
                parentLine: l,
                tickerInfo: selectedTickersInfo,
              },
              scale: meta.scaleType === 1 ? "abs" : "index",
              visible: dataObj.length > 0 ? true : false,
              isSecondaryAxis: meta.isSecondaryAxis,
              data: dataObj,
            };
            if (meta.isSecondaryAxis === true) {
              obj["yAxis"] = lineIndex;
            } else {
              obj["yAxis"] = 0;
            }
            seriesData.push(obj);
          }
        });
      });
    } else {
      meta.lines.forEach((l, lineIndex) => {
        const lineName = meta.lineInfo[l].name;
        seriesData.push({
          name: lineName,
          custom: {
            isLineItem: true,
            lineId: l,
          },
          color: "transparent",
          className: "lineitem",
          data: [],
        });
        for (let i = 0; i < 5; i++) {
          seriesData.push({
            name: "",
            color: "transparent",
            data: [],
          });
        }
        chartPeriods.forEach((p) => {
          const loopLimit = 6;
          dashStyleIndex = dashStyleIndex === 6 ? 0 : dashStyleIndex + 1;
          for (
            let numRows = 0;
            numRows < (meta.tickers.length > 6 ? 2 : 1);
            numRows++
          ) {
            for (let i = 0; i < loopLimit; i++) {
              const index = i + numRows * loopLimit;
              const dataIndex = index + meta.tickers.length * lineIndex;
              if (index > meta.tickers.length - 1) {
                seriesData.push({
                  name: "",
                  color: "transparent",
                  data: [],
                });
              } else {
                let dataObj = [];
                const t = meta.tickers[index];
                if (
                  data[dataIndex] &&
                  data[dataIndex].data[t] &&
                  data[dataIndex].data[t][l] &&
                  data[dataIndex].data[t][l][p]
                ) {
                  const earningsDate =
                    data[dataIndex].data[t][l]["earnings_date"];
                  const earningsDateWithRev =
                    data[dataIndex].data[t][l]["earnings_date_w_rev"];
                  dataObj = data[dataIndex].data[t][l][p]["values"].map(
                    (v, valueIndex) => {
                      const date = v[0].split("-");
                      if (earningsDate && earningsDate.indexOf(v[0]) !== -1) {
                        return {
                          x: new Date(
                            Number(date[0]),
                            Number(date[1]) - 1,
                            Number(date[2]),
                          ).getTime(),
                          y: v[meta.scaleType],
                          abs: v[1],
                          ind: v[2],
                          marker: {
                            enabled: true,
                            fillColor: "#fc762b",
                            symbol: "Circle",
                          },
                          ticker: t,
                          period: p,
                          isEarningsDate: true,
                          isEarningsDateWithRev:
                            earningsDateWithRev &&
                            earningsDateWithRev.indexOf(v[0]) !== -1,
                          lineIndex: lineIndex,
                          brokerCount:
                            data[dataIndex].data[t][l][p]["broker_count_list"][
                              valueIndex
                            ],
                        };
                      }
                      return {
                        x: new Date(
                          Number(date[0]),
                          Number(date[1]) - 1,
                          Number(date[2]),
                        ).getTime(),
                        y: v[meta.scaleType],
                        abs: v[1],
                        ind: v[2],
                        marker: {
                          //Enable marker if current datapoint has no left neighbour and right neighbour
                          enabled:
                            (!data[dataIndex].data[t][l][p]["values"][
                              valueIndex - 1
                            ] &&
                              data[dataIndex].data[t][l][p]["values"][
                                valueIndex + 1
                              ] &&
                              data[dataIndex].data[t][l][p]["values"][
                                valueIndex + 1
                              ].length === 1) ||
                            (!data[dataIndex].data[t][l][p]["values"][
                              valueIndex + 1
                            ] &&
                              data[dataIndex].data[t][l][p]["values"][
                                valueIndex - 1
                              ] &&
                              data[dataIndex].data[t][l][p]["values"][
                                valueIndex - 1
                              ].length === 1) ||
                            (data[dataIndex].data[t][l][p]["values"][
                              valueIndex - 1
                            ] &&
                              data[dataIndex].data[t][l][p]["values"][
                                valueIndex - 1
                              ].length === 1 &&
                              data[dataIndex].data[t][l][p]["values"][
                                valueIndex + 1
                              ] &&
                              data[dataIndex].data[t][l][p]["values"][
                                valueIndex + 1
                              ].length === 1),
                          radius: 2,
                        },
                        ticker: t,
                        period: p,
                        isEarningsDate: false,
                        lineIndex: lineIndex,
                        brokerCount:
                          data[dataIndex].data[t][l][p]["broker_count_list"][
                            valueIndex
                          ],
                      };
                    },
                  );
                }
                dataObj = dataObj.sort((a, b) => a.x - b.x);
                const seriesName = `${t}-${p}`;
                const selectedTickersInfo = meta.selectedTickersInfo
                  ? { ...meta.selectedTickersInfo[index], period: p }
                  : undefined;
                const obj = {
                  type: meta.lineInfo[l].isValuationRatio ? "spline" : "line",
                  name: seriesName,
                  dashStyle:
                    p === yearWithSolid ? "solid" : lineType[dashStyleIndex],
                  color: meta.legendColors[index + 6 * lineIndex],
                  marker: {
                    enabled: dataObj.length === 1 ? true : false,
                    radius: 4,
                  },
                  custom: {
                    parentLine: l,
                    tickerInfo: selectedTickersInfo,
                  },
                  scale: meta.scaleType === 1 ? "abs" : "index",
                  visible: dataObj.length > 0 ? true : false,
                  isSecondaryAxis: meta.isSecondaryAxis,
                  data: dataObj,
                };
                if (meta.isSecondaryAxis === true) {
                  obj["yAxis"] = lineIndex;
                } else {
                  obj["yAxis"] = 0;
                }
                seriesData.push(obj);
              }
            }
          }
        });
      });
    }
    const formatterFunction = this.formatCell;
    const dateFormatter = this.formateDate;
    const alertMessage = this.message;
    let legendOptions: Highcharts.LegendOptions = {
      align: "left",
      layout: "horizontal",
      itemMarginBottom: 4,
      verticalAlign: "bottom",
      margin: 28,
      maxHeight: 85,
      itemDistance: 10,
      useHTML: true,
      symbolWidth: 44,
      labelFormatter: function () {
        const { custom } = (this as any).userOptions;
        const hoverTitle =
          custom &&
          custom.tickerInfo &&
          custom.tickerInfo.name &&
          custom.tickerInfo.ticker
            ? `${custom.tickerInfo.ticker} - ${custom.tickerInfo.name} ${
                custom.tickerInfo.period ? `- ${custom.tickerInfo.period}` : ""
              }`
            : this.name;
        return `<span title="${hoverTitle}">${this.name}</span>`;
      },
    };
    if (isComparativePageChart) {
      legendOptions = {
        ...legendOptions,
        maxHeight: 250,
        align: "left",
        layout: "vertical",
        itemMarginTop: 4,
        margin: 2,
        verticalAlign: "top",
        y: 20,
        itemDistance: undefined,
      };
    }
    const chartOptions: Highcharts.Options = {
      chart: {
        events: {
          load: function () {
            if (this.options.chart["forExport"]) {
              this.legend.options.maxHeight = undefined;
            } else {
              const itemDistance = this.legend.options.itemDistance;
              const maxItemWidth = this.legend["maxItemWidth"];

              let rowWidth = maxItemWidth * 6 + itemDistance * 5;
              let itemWidth = maxItemWidth;

              const availableWidth = this["chartWidth"];
              if (rowWidth > availableWidth) {
                itemWidth =
                  itemWidth - Math.ceil((rowWidth - availableWidth) / 6);
                rowWidth = availableWidth;
              }
              this.legend.options.width = rowWidth;
              this.legend.options.itemWidth = itemWidth;
              this.legend.options.itemHoverStyle = {};
              this.legend.options.itemStyle = {
                textOverflow: "ellipsis",
              };
              this.legend.update({ ...this.legend.options }, true);
            }
          },
        },
      },
      title: {
        text: "",
      },

      subtitle: {
        text: "",
      },

      yAxis: this.getYAxisOptions(meta.isSecondaryAxis, meta),
      xAxis: {
        type: "datetime",
        // min: dateFormatter(
        //   "min",
        //   meta.revision.startsWith("custom") ? meta.revision.split("|")[1] :
        //   parseInt(meta.revision.split("m")[0], 10) + 1,
        //   meta.revision.startsWith("custom") ? true : false
        // ),
        // max: meta.revision.startsWith("custom") ? Date.UTC(parseInt(meta.revision.split('|')[1].split('-')[2],10), parseInt(meta.revision.split('|')[1].split('-')[0],10)) : dateFormatter("max", 6, false),

        min: meta.revision.startsWith("custom")
          ? Date.UTC(
              parseInt(meta.revision.split("|")[1].split("-")[2], 10),
              parseInt(meta.revision.split("|")[1].split("-")[0], 10) - 2,
            )
          : dateFormatter("min", parseInt(meta.revision.split("m")[0], 10) + 1),
        max: dateFormatter("max", 6),
        tickInterval: 365 * 24 * 3600 * 60,
        labels: {
          format: "{value:%b %Y}",
        },
      },

      tooltip: {
        headerFormat: "",
        backgroundColor: "rgba(248, 248,248,1)",
        useHTML: true,
        outside: true,
        // className: 'charts-page-tooltip',
        formatter: function () {
          const xDate = Highcharts.dateFormat("%d-%b-%Y", Number(this.x));
          let tooltipHtml = `<div>${this.point["ticker"]} ${this.point["period"]}<br>`;
          // const lineIndex = this.point["lineIndex"];
          let lineIndex = 0;
          if (this.point["lineIndex"] !== undefined) {
            lineIndex = this.point["lineIndex"];
          }
          const indexVal =
            this.point.series["userOptions"]["scale"] !== "index"
              ? ""
              : `Indexed Value: ${this.point["ind"]}<br>`;
          const formattedValue = formatterFunction(
            this.point["abs"],
            meta.lineInfo[meta.lines[lineIndex]].curr_symbol,
            meta.lineInfo[meta.lines[lineIndex]].unit,
            "values",
          );
          tooltipHtml += `${
            meta.lineInfo[meta.lines[lineIndex]].name
          }: ${formattedValue}${
            meta.lineInfo[meta.lines[lineIndex]].magnitude !== "Abs" &&
            meta.lineInfo[meta.lines[lineIndex]].magnitude !== undefined
              ? meta.lineInfo[meta.lines[lineIndex]].magnitude
              : ""
          }<br>`;
          tooltipHtml += `${indexVal}`;
          if (this.point["ticker"] !== "Average") {
            if (this.point["isEarningsDate"] === true) {
              tooltipHtml += `Earnings Date: ${xDate}<br>`;
              if (this.point["isEarningsDateWithRev"] === true) {
                const brokerCount = this.point["brokerCount"];
                tooltipHtml += `Revision date: ${xDate}<br># of brokers: ${
                  brokerCount === null ? "NA" : brokerCount
                }`;
              }
            } else {
              const brokerCount = this.point["brokerCount"];
              tooltipHtml += `Revision date: ${xDate}<br># of brokers: ${
                brokerCount === null ? "NA" : brokerCount
              }`;
            }
          }
          tooltipHtml += "</div>";
          return tooltipHtml;
        },
        //   style: {
        //     zIndex: 9999,
        //     opacity: 1,
        // }
      },
      legend: legendOptions,
      plotOptions: {
        line: {
          step: "left",
        },
        series: {
          events: {
            legendItemClick: function () {
              if (this["userOptions"].custom.isLineItem === true) {
                const lineId = this["userOptions"].custom.lineId;
                const shouldHide = this.visible;

                Highcharts.each(this.chart.series, function (s) {
                  if (
                    s["userOptions"].custom &&
                    s["userOptions"].custom.parentLine &&
                    s["userOptions"].custom.parentLine === lineId
                  ) {
                    if (s.data.length) {
                      if (shouldHide) {
                        s.setVisible(false, false);
                      } else {
                        s.setVisible(true, false);
                      }
                    }
                  }
                });
                this.chart.redraw();
              } else {
                if (this.data.length === 0) {
                  alertMessage.warning("No data available.");
                  return false;
                }
              }
              return true;
            },
          },
        },
      },
      colors: meta.legendColors,
      series: seriesData,
    };
    return chartOptions;
  }

  getChartsPageBarOptions(meta) {
    const seriesData = [];
    let dataObj = {};
    if (meta.scaleType === 1) {
      dataObj = meta.absVal;
    } else {
      dataObj = meta.indexVal;
    }
    meta.lines.forEach((l, lineIndex) => {
      const lineName = meta.lineInfo[l].name;
      const dataArr = [];
      let hasNoData = true;
      meta.tickers.forEach((t, tickerIndex) => {
        const objData = {
          y: dataObj[l][meta.dataView][tickerIndex],
          opacity: 1,
          name: `${l}-${t}`,
          lineIndex: lineIndex,
          type: meta.typeVal[l][tickerIndex],
          tickerInfo: meta.selectedTickersInfo
            ? meta.selectedTickersInfo[tickerIndex]
            : undefined,
        };
        dataArr.push(objData);
        if (objData.y) {
          hasNoData = false;
        }
      });

      const obj = {
        type: "column",
        name: lineName,
        color: meta.legendColors[lineIndex],
        dv: meta.dataView,
        scale: meta.scaleType === 1 ? "abs" : "index",
        isSecondaryAxis: meta.isSecondaryAxis,
        marker: {
          enabled: true,
        },
        custom: {
          parentLine: l,
          zero_align_axis: true,
          hasNoData: hasNoData,
        },
        visible: hasNoData ? false : true,
        data: dataArr,
      };
      seriesData.push(obj);
      if (meta.isSecondaryAxis === true) {
        obj["yAxis"] = lineIndex;
      } else {
        obj["yAxis"] = 0;
      }
    });
    const formatterFunction = this.formatCell;
    const alertMessage = this.message;
    const chartOptions: Highcharts.Options = {
      title: {
        text: "",
      },

      subtitle: {
        text: "",
      },

      yAxis: this.getYAxisOptions(meta.isSecondaryAxis, meta),
      xAxis: {
        categories: meta.tickers,
        labels: {
          useHTML: true,
          formatter: function () {
            const { value } = this;
            const tickerInfo =
              (meta.selectedTickersInfo || []).find(
                (item) => item.ticker === value,
              ) || {};
            const title =
              tickerInfo.name && tickerInfo.ticker
                ? `${tickerInfo.ticker} - ${tickerInfo.name}`
                : value;
            return `<span title="${title}">${value}</span>`;
          },
        },
      },
      lang: {
        noData: "No Data Available",
      },
      noData: {
        style: {
          fontWeight: "bold",
          fontSize: "15px",
          color: "#303030",
        },
      },

      tooltip: {
        headerFormat: "",
        backgroundColor: "rgba(248, 248,248,1)",
        useHTML: true,
        outside: true,
        formatter: function () {
          let tooltipHtml = "";
          const ticker = this.x;
          const tickerInfo =
            (meta.selectedTickersInfo || []).find(
              (item) => item.ticker === ticker,
            ) || {};
          const tickerFullName = tickerInfo.name;
          const type = this.point.options["type"];
          const lineId = parseInt(this.key.toString().split("-")[0], 10);
          const lineData = meta.lineInfo[lineId];
          const currSymbol = lineData
            ? lineData.currSymbol || lineData.curr_symbol
            : null;
          const unit = lineData ? lineData.unit : null;
          const magnitude =
            lineData && lineData.magnitude
              ? lineData.magnitude !== "Abs" &&
                this.point.series["userOptions"]["dv"] !== "yoy"
                ? lineData.magnitude
                : ""
              : "";

          const indexVal =
            this.point.series["userOptions"]["scale"] !== "index"
              ? ""
              : `Indexed Value: ${meta.indexVal[lineId][meta.dataView][
                  this.point.index
                ].toFixed(2)}`;
          const absFormattedValue = formatterFunction(
            meta.absVal[this.key.toString().split("-")[0]][
              this.point.series["userOptions"]["dv"]
            ][this.point.index],
            currSymbol,
            unit,
            this.point.series["userOptions"]["scale"] === "index"
              ? this.point.series["userOptions"]["scale"]
              : this.point.series["userOptions"]["dv"],
          );
          tooltipHtml += `${ticker} ${
            tickerFullName ? `- ${tickerFullName}` : ""
          } - ${type} <br/>${
            lineData.name
          }: ${absFormattedValue}${magnitude}<br/>
           ${indexVal}`;
          //  meta.absVal[this.key.split('-')[0]][this.point.series["userOptions"]["dv"]][this.point.index]
          return tooltipHtml;
        },
      },

      legend: {
        align: "left",
        layout: "horizontal",
        itemMarginBottom: 4,
        verticalAlign: "bottom",
        margin: 28,
        maxHeight: 85,
        itemDistance: 10,
        useHTML: true,
        symbolWidth: 44,
        labelFormatter: function () {
          return `<span title="${this.name}">${this.name}</span>`;
        },
      },

      plotOptions: {
        column: {
          pointPadding: 0.1,
          borderWidth: 0,
          minPointLength: 3,
        },
        series: {
          events: {
            legendItemClick: function () {
              if (this["userOptions"].custom.hasNoData === true) {
                alertMessage.warning("No data available.");
                return false;
              }
              return true;
            },
          },
        },
      },
      colors: meta.legendColors,
      series: seriesData,
    };

    return chartOptions;
  }

  getChartsPageTimeseriesOptions(meta) {
    const seriesData = [];
    const dataObj = {};
    meta.scaleType === 1
      ? Object.assign(dataObj, meta.absVal)
      : Object.assign(dataObj, meta.indexVal);
    const isComparativePageChart = meta.isComparativePageChart;

    if (isComparativePageChart) {
      meta.lines.forEach((l) => {
        for (let i = 0; i < meta.tickers.length; i++) {
          const t = meta.tickers[i];
          let hasNoData = true;
          const objData =
            dataObj[l][meta.dataView][t] &&
            dataObj[l][meta.dataView][t].map((d, index) => {
              if (d) {
                hasNoData = false;
              }
              return {
                y: d,
                opacity: 1,
                name: `${l}-${t}`,
                isSecondaryAxis: meta.isSecondaryAxis,
                lineIndex: index,
                type: meta.typeVal[l][index],
              };
            });

          if (objData) {
            const zoneIndex = this.getZoneIndexForEstimatedPeriods(
              meta["x"],
              meta.respectivePeriodData[l][t],
            );
            const obj = {
              type: "line",
              name: t,
              // color: meta.legendColors[i + 6 * lineIndex],
              dv: meta.dataView,
              scale: meta.scaleType === 1 ? "abs" : "index",
              dashStyle: "solid",
              marker: {
                enabled: true,
                radius: 4,
              },
              custom: {
                parentLine: l,
                hasNoData: hasNoData,
                tickerInfo: meta.selectedTickersInfo
                  ? meta.selectedTickersInfo[i]
                  : undefined,
              },
              // yAxis: i,
              visible: hasNoData ? false : true,
              data: objData,
              zoneAxis: "x",
              zones: [
                {
                  value: zoneIndex,
                },
                {
                  dashStyle: "dash",
                },
              ],
            };
            obj["yAxis"] = 0;
            seriesData.push(obj);
          }
        }
      });
    } else {
      meta.lines.forEach((l, lineIndex) => {
        const lineName = meta.lineInfo[l].name;
        seriesData.push({
          name: lineName,
          custom: {
            isLineItem: true,
            lineId: l,
          },
          color: "transparent",
          className: "lineitem",
          data: [],
        });
        for (let i = 0; i < 5; i++) {
          seriesData.push({
            name: "",
            color: "transparent",
            data: [],
          });
        }
        const loopLimit = 6;

        for (
          let numRows = 0;
          numRows < (meta.tickers.length > 6 ? 2 : 1);
          numRows++
        ) {
          for (let i = 0; i < loopLimit; i++) {
            const index = i + numRows * loopLimit;
            if (index > meta.tickers.length - 1) {
              seriesData.push({
                name: "",
                color: "transparent",
                data: [],
              });
            } else {
              const t = meta.tickers[index];
              let hasNoData = true;
              const objData =
                dataObj[l][meta.dataView][t] &&
                dataObj[l][meta.dataView][t].map((d, index) => {
                  if (d) {
                    hasNoData = false;
                  }
                  return {
                    y: d,
                    opacity: 1,
                    name: `${l}-${t}`,
                    isSecondaryAxis: meta.isSecondaryAxis,
                    lineIndex: index,
                    type: meta.typeVal[l][index],
                  };
                });

              if (objData) {
                const zoneIndex = this.getZoneIndexForEstimatedPeriods(
                  meta["x"],
                  meta.respectivePeriodData[l][t],
                );
                const obj = {
                  type: "line",
                  name: t,
                  color: meta.legendColors[index + 6 * lineIndex],
                  dv: meta.dataView,
                  scale: meta.scaleType === 1 ? "abs" : "index",
                  dashStyle: "solid",
                  marker: {
                    enabled: true,
                    radius: 4,
                  },
                  custom: {
                    parentLine: l,
                    hasNoData: hasNoData,
                    tickerInfo: meta.selectedTickersInfo
                      ? meta.selectedTickersInfo[index]
                      : undefined,
                  },
                  // yAxis: i,
                  visible: hasNoData ? false : true,
                  data: objData,
                  zoneAxis: "x",
                  zones: [
                    {
                      value: zoneIndex,
                    },
                    {
                      dashStyle: "dash",
                    },
                  ],
                };
                if (meta.isSecondaryAxis === true) {
                  obj["yAxis"] = lineIndex;
                } else {
                  obj["yAxis"] = 0;
                }
                seriesData.push(obj);
              }
            }
          }
        }
      });
    }

    const formatterFunction = this.formatCell;
    const alertMessage = this.message;
    let legendOptions: Highcharts.LegendOptions = {
      align: "left",
      layout: "horizontal",
      itemMarginBottom: 4,
      verticalAlign: "bottom",
      margin: 28,
      maxHeight: 85,
      itemDistance: 10,
      useHTML: true,
      symbolWidth: 44,
      labelFormatter: function () {
        const { custom } = (this as any).userOptions;
        const hoverTitle =
          custom &&
          custom.tickerInfo &&
          custom.tickerInfo.name &&
          custom.tickerInfo.ticker
            ? `${custom.tickerInfo.ticker} - ${custom.tickerInfo.name}`
            : this.name;
        return `<span title="${hoverTitle}">${this.name}</span>`;
      },
    };
    if (isComparativePageChart) {
      legendOptions = {
        ...legendOptions,
        maxHeight: 250,
        align: "left",
        layout: "vertical",
        itemMarginTop: 4,
        margin: 2,
        verticalAlign: "top",
        y: 20,
        itemDistance: undefined,
      };
    }
    const chartOptions: Highcharts.Options = {
      chart: {
        events: {
          load: function () {
            const itemDistance = this.legend.options.itemDistance;
            const maxItemWidth = this.legend["maxItemWidth"];

            let rowWidth = maxItemWidth * 6 + itemDistance * 5;
            let itemWidth = maxItemWidth;

            const availableWidth = this["chartWidth"];
            if (rowWidth > availableWidth) {
              itemWidth =
                itemWidth - Math.ceil((rowWidth - availableWidth) / 6);
              rowWidth = availableWidth;
            }
            this.legend.options.width = rowWidth;
            this.legend.options.itemWidth = itemWidth;
            this.legend.options.itemHoverStyle = {};
            this.legend.options.itemStyle = {
              textOverflow: "ellipsis",
            };
            this.legend.update({ ...this.legend.options }, true);
          },
        },
      },
      title: {
        text: "",
      },

      subtitle: {
        text: "",
      },

      yAxis: this.getYAxisOptions(meta.isSecondaryAxis, meta),
      xAxis: {
        categories: meta["x"],
        min: 0,
      },

      tooltip: {
        headerFormat: "",
        backgroundColor: "rgba(248, 248,248,1)",
        useHTML: true,
        outside: true,
        formatter: function () {
          let tooltipHtml = "";
          const ticker = this.point.series.name;
          const year = this.point["category"].toString().includes("E")
            ? this.point["category"]
                .toString()
                .substring(0, this.point["category"].toString().length - 1)
            : this.point["category"];
          const type = meta.typeVal[this.key.toString().split("-")[0]][
            this.series["userOptions"].name
          ][meta.x.indexOf(this.x)]
            ? meta.typeVal[this.key.toString().split("-")[0]][
                this.series["userOptions"].name
              ][meta.x.indexOf(this.x)]
            : "";
          const lineId = parseInt(this.key.toString().split("-")[0], 10);
          const lineData = meta.lineInfo[lineId];
          const currSymbol = lineData
            ? lineData.currSymbol || lineData.curr_symbol
            : null;
          const unit = lineData ? lineData.unit : null;
          const magnitude =
            lineData && lineData.magnitude
              ? lineData.magnitude !== "Abs" &&
                this.point.series["userOptions"]["dv"] !== "yoy"
                ? lineData.magnitude
                : ""
              : "";

          const indexVal =
            this.point.series["userOptions"]["scale"] !== "index"
              ? ""
              : `Indexed Value: ${meta.indexVal[lineId][meta.dataView][
                  this.point.series.name
                ][this.point.index].toFixed(2)}`;
          const formattedValue = formatterFunction(
            meta.absVal[this.key.toString().split("-")[0]][
              this.point.series["userOptions"]["dv"]
            ][this.key.toString().substr(this.key.toString().indexOf("-") + 1)][
              this.point.index
            ],
            currSymbol,
            unit,
            this.point.series["userOptions"]["scale"] === "index"
              ? this.point.series["userOptions"]["scale"]
              : this.point.series["userOptions"]["dv"],
          );
          // note: this does not work with relative period
          // const displayYear = year
          //   .toString()
          //   .split("-")[0]
          //   .includes("Q")
          //   ? year
          //   : year.toString().split("-")[1];
          tooltipHtml += `${ticker} ${year} - ${type} <br/>${lineData.name}: ${formattedValue}${magnitude}<br/>
           ${indexVal}`;

          return tooltipHtml;
        },
      },

      legend: legendOptions,
      plotOptions: {
        series: {
          events: {
            legendItemClick: function () {
              if (this["userOptions"].custom.isLineItem === true) {
                const lineId = this["userOptions"].custom.lineId;
                const shouldHide = this.visible;

                Highcharts.each(this.chart.series, function (s) {
                  if (
                    s["userOptions"].custom &&
                    s["userOptions"].custom.parentLine &&
                    s["userOptions"].custom.parentLine === lineId
                  ) {
                    if (!s["userOptions"].custom.hasNoData) {
                      if (shouldHide) {
                        s.setVisible(false, false);
                      } else {
                        s.setVisible(true, false);
                      }
                    }
                  }
                });
                this.chart.redraw();
              } else {
                if (this["userOptions"].custom.hasNoData === true) {
                  alertMessage.warning("No data available.");
                  return false;
                }
              }
              return true;
            },
          },
        },
      },
      colors: meta.legendColors,
      series: seriesData,
    };
    return chartOptions;
  }

  getMiniRevisionDataOptions(dataSet) {
    const seriesObj = [];
    for (const obj in dataSet.data) {
      const sourceObj = {
        name: obj,
        data: [],
        type: "line",
        fillColor: {
          linearGradient: [0, 0, 0, 300],
        },
        marker: {
          enabled: true,
        },
      };

      const dataObj = this.formatMonitoringRevisionData(
        dataSet.data["CONSENSUS"],
        dataSet.filingsPeriod,
      );
      sourceObj.data = dataObj;
      if (sourceObj.data.length === 0) {
        sourceObj.name = `${obj}(data unavailable)`;
      } else {
        seriesObj.push(sourceObj);
      }
    }
    const chartOptions: Highcharts.Options = {
      chart: {
        type: "line",
        margin: [5, 5, 5, 5],
        borderWidth: 0,
        animation: false,
        backgroundColor: "rgba(255, 255, 255, 0.002)",
      },
      credits: { enabled: false },
      title: { text: "" },
      tooltip: {
        enabled: false,
      },

      legend: { enabled: false },
      xAxis: {
        type: "datetime",
        title: { text: "" },
        lineWidth: 0,
        tickWidth: 0,
        labels: {
          enabled: false,
        },
        minRange: 7 * 24 * 3600 * 1000,
      },

      yAxis: {
        title: { text: "" },
        lineWidth: 0,
        tickWidth: 0,
        gridLineWidth: 0,
        labels: {
          enabled: false,
        },
      },

      plotOptions: {
        line: {
          stacking: "normal",
          step: "left",
        },
        series: {
          enableMouseTracking: false,
          shadow: false,
          marker: {
            enabled: false,
          },
          borderWidth: 0,
          animation: false,
        },
      },

      series: seriesObj,
    };

    return chartOptions;
  }

  getMultiCompanyBubbleChartOptions(dataSet) {
    const bubbleChartOptions: Highcharts.Options = {
      chart: {
        type: "bubble",
        zoomType: "xy",
      },
      legend: {
        enabled: true,
      },
      title: {
        text: this.showTitle
          ? `<span title="${dataSet.x} vs. ${dataSet.y}">${dataSet.x} vs. ${dataSet.y}</span>`
          : "",
        useHTML: true,
        align: "left",
        y: 25,
      },
      xAxis: {
        gridLineWidth: 1,
        title: {
          text: dataSet.x,
        },
        labels: {
          format: `${dataSet.xc}{value}${
            dataSet.xu !== "Amount" ? dataSet.xu : ""
          }`,
        },
      },

      yAxis: {
        startOnTick: false,
        endOnTick: false,
        title: {
          text: dataSet.y,
        },
        labels: {
          format: `${dataSet.yc}{value:.2f}${
            dataSet.yu !== "Amount" ? dataSet.yu : ""
          }`,
        },
        maxPadding: 0.2,
      },

      tooltip: {
        enabled: true,
        useHTML: true,
        formatter: function () {
          let toolTipHTML = '<div class="tooltip">';
          toolTipHTML +=
            `<div><span><b>${this.point.name}: </b></span><span>${
              dataSet.data[this.point.index].full_name
            }</span></div>` +
            `<div><span><b>${dataSet.x}: </b></span><span> ${
              dataSet.xc
            }${Highcharts.numberFormat(
              dataSet.data[this.point.index].x,
              2,
              ".",
              ",",
            )} ${dataSet.xu !== "Amount" ? dataSet.xu : ""}</span></div>` +
            `<div><span><b>${dataSet.y}: </b></span><span>${
              dataSet.yc
            }${Highcharts.numberFormat(
              dataSet.data[this.point.index].y,
              2,
              ".",
              ",",
            )} ${dataSet.yu !== "Amount" ? dataSet.yu : ""}</span></div>` +
            `<div><span><b>${dataSet.z}: </b></span><span>${
              dataSet.zc
            }${Highcharts.numberFormat(
              dataSet.data[this.point.index].z,
              2,
              ".",
              ",",
            )} ${dataSet.zu !== "Amount" ? dataSet.zu : ""}</span></div>`;
          toolTipHTML += "</div>";

          return toolTipHTML;
        },
      },
      plotOptions: {
        series: {
          dataLabels: {
            enabled: true,
            format: "{point.name}",
          },
        },
      },
      credits: {
        enabled: false,
      },
      series: [
        {
          data: dataSet.data,
          name: dataSet.z,
          type: "bubble",
        },
      ],
    };
    return bubbleChartOptions;
  }

  // Chart types to be done for company page
  // - Distribution Analysis
  // - Distribution Range
  // - Trend Bar
  // - Revsion Daily
  // - Revsion Monthly
  // - Revsion Analysis

  getDistributionAnalysisChartOptions(dataSet, chartTypeDisplay: string) {
    this.chartTypeDisplay = chartTypeDisplay;
    const tooltipColumnData = {};
    const objData = dataSet.Value;
    for (let i = 0; i < objData.data.broker_value_list.length; i++) {
      tooltipColumnData[objData.data.broker_value_list[i][0]] = {
        percent: objData.data.broker_value_list[i][2],
        actualBrokerEstimate: objData.data.broker_value_list[i][3],
        difference: objData.data.broker_value_list[i][1],
      };
    }
    const dAnalysisChartOptions: Highcharts.Options = {
      chart: {
        type: "column",
        inverted: true,
      },
      xAxis: {
        categories: objData.x,
        opposite: true,
        tickWidth: 0,
      },
      yAxis: {
        plotLines: [
          {
            color: "#000000",
            width: 1,
            value: 0,
            label: {
              verticalAlign: "top",
              rotation: 0,
              y: 0,
              x: 0,
              style: {
                color: "#000",
                fontWeight: "bold",
              },
              text: `Consensus = ${dataSet.consensusvalue}`,
            },
          },
        ],
        title: {
          text: "",
        },
        lineWidth: 0,
        gridLineWidth: 0,
        labels: {
          enabled: false,
        },
      },
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        headerFormat: "",
        borderWidth: 0,
        formatter: function () {
          const point = tooltipColumnData[this.key];
          const unitSymbol = dataSet.unit === "%" ? "bps" : "%";
          let isAmount = false;
          if (dataSet.unit === "Amount") {
            isAmount = true;
          }

          let tooltipHtml = "";
          tooltipHtml += "Difference from consensus: ";
          if (isAmount === true) {
            if (point.difference >= 0)
              tooltipHtml += dataSet.display_unit + point.difference;
            else
              tooltipHtml += `-${dataSet.display_unit}${Math.abs(
                point.difference,
              )}`;
          } else {
            tooltipHtml += point.difference + dataSet.display_unit;
          }
          tooltipHtml += ` (${point.percent}${unitSymbol})`;
          tooltipHtml += `<br>Source: ${this.key}`;

          return tooltipHtml;
        },
      },
      plotOptions: {
        series: {
          color: "#3AAC6C",
          negativeColor: "#C23824",
          dataLabels: {
            verticalAlign: "middle",
            enabled: true,
            formatter: function () {
              let point =
                tooltipColumnData[this.point.name].actualBrokerEstimate;
              if (parseFloat(point) < 0) {
                point = `(${Highcharts.numberFormat(
                  Math.abs(point),
                  dataSet.precision,
                  ".",
                  ",",
                )})`;
              } else {
                point = Highcharts.numberFormat(
                  point,
                  dataSet.precision,
                  ".",
                  ",",
                );
              }

              let isAmount = false;
              if (dataSet.unit === "Amount") {
                isAmount = true;
              }

              let tooltipHtml = "";
              tooltipHtml +=
                isAmount === true
                  ? dataSet.display_unit + point
                  : point + dataSet.display_unit;

              return tooltipHtml;
            },
          },
        },
      },
      series: [{ data: objData.data.broker_value_list, type: "column" }],
    };
    let exportOptions: Highcharts.ExportingOptions = {};
    this.seriesInfo = dataSet;
    if (dataSet.export) {
      exportOptions = this.getExportSettings(dataSet, "distribution-analysis");
      dAnalysisChartOptions["exporting"] = exportOptions;
    }
    return dAnalysisChartOptions;
  }

  getDistibutionRangeChartOptions(dataSet, chartTypeDisplay: string) {
    this.chartTypeDisplay = chartTypeDisplay;
    const tooltipData = {};
    const objData = dataSet.Value;

    for (let i = 0; i < objData.data.lowhigh.length; i++) {
      tooltipData[objData.data.lowhigh[i][0]] = {
        low:
          objData.data.lowhigh[i][1] !== null
            ? objData.data.lowhigh[i][1]
            : "NA",
        high:
          objData.data.lowhigh[i][2] !== null
            ? objData.data.lowhigh[i][2]
            : "NA",
        consensus: objData.data.normal[i][1],
      };
    }
    const dRangeOption: Highcharts.Options = {
      chart: {},
      xAxis: {
        gridLineWidth: 0,
        title: {
          text: "",
        },
        categories: objData.x,
      },
      yAxis: {
        title: {
          text: "",
        },
      },
      legend: {
        itemDistance: 50,
      },

      series: [
        {
          name: "High/Low",
          type: "arearange",
          data: objData.data.lowhigh,
        },
        {
          name: "Consensus",
          type: "line",
          color: Highcharts.getOptions().colors[1],
          data: objData.data.normal,
        },
      ],

      tooltip: {
        shared: true,
        useHTML: true,
        formatter: function () {
          const point = tooltipData[this.x];
          if (!point) {
            return "";
          }
          const hasSource = dataSet.source.toLowerCase() !== "consensus";
          let s = `<span><span>Period : ${this.x}`;
          if (hasSource) {
            s += `</span><br/><span>Consensus : ${point.consensus}</span><br/>`;
          } else {
            s += `</span><br/><span>${dataSet.source} : ${point.consensus}</span><br/>`;
          }
          s +=
            `<span>High : ${point.high}</span><br/>` +
            `<span>Low : ${point.low}</span></span>`;
          return s;
        },
      },
    };
    let exportOptions: Highcharts.ExportingOptions = {};
    this.seriesInfo = dataSet;
    if (dataSet.export) {
      exportOptions = this.getExportSettings(dataSet, "distribution-range");
      dRangeOption["exporting"] = exportOptions;
    }
    return dRangeOption;
  }

  getTrendBarChartOptions(dataSet, chartTypeDisplay: string) {
    this.chartTypeDisplay = chartTypeDisplay;
    const tooltipTrendData = {
      unit: dataSet.unit,
      currency: dataSet.currency,
      display_unit: dataSet.display_unit,
    };
    const dataObj = dataSet.Value.data;
    dataObj.normal = dataObj.normal.map((_normal) => {
      if (_normal[1] === "") {
        _normal[1] = null;
      }
      return _normal;
    });
    for (let i = 0; i < dataObj.normal.length; i++) {
      tooltipTrendData[dataObj.normal[i][0]] = {
        yoy:
          dataObj.yoy &&
          dataObj.yoy.length > i &&
          dataObj.yoy[i] &&
          dataObj.yoy[i][1] !== null
            ? Highcharts.numberFormat(
                dataObj.yoy[i][1],
                dataSet.precision,
                ".",
                ",",
              )
            : null,
        consensus: Highcharts.numberFormat(
          dataObj.normal[i][1],
          dataSet.precision,
          ".",
          ",",
        ),
        source: dataObj.normal[i][2],
      };
    }
    let titleText = "";
    const hasSource = dataSet.source.toLowerCase() !== "consensus";

    if (hasSource) {
      titleText = `${dataSet.source} `;
    } else {
      titleText = "Consensus/VA actual ";
    }

    titleText += `(in ${dataSet.display_unit})`;

    const baseCurrencyTitle =
      dataSet.baseCurrency && dataSet.baseCurrFromMEData !== "Base"
        ? `YOY Growth (in ${dataSet.baseCurrency})`
        : "YOY Growth";
    const yAxisArr: Array<any> = [
      {
        // Primary yAxis
        labels: {
          formatter: function () {
            return Highcharts.numberFormat(
              this.value,
              dataSet.precision,
              ".",
              ",",
            );
          },
          style: {
            color: Highcharts.getOptions().colors[0],
          },
        },
        title: {
          text: titleText,
          style: {
            color: Highcharts.getOptions().colors[0],
          },
        },
      },
    ];
    if (dataObj.yoy && dataObj.yoy.length) {
      yAxisArr.push({
        // Secondary yAxis
        gridLineWidth: 0,
        title: {
          text: baseCurrencyTitle,
          style: {
            color: Highcharts.getOptions().colors[1],
          },
        },
        labels: {
          formatter: function () {
            const unitSymbol = dataSet.unit === "%" ? "bps" : "%";
            return (
              Highcharts.numberFormat(this.value, dataSet.precision, ".", ",") +
              unitSymbol
            );
          },
          style: {
            color: Highcharts.getOptions().colors[1],
          },
        },
        opposite: true,
      });
    }

    const seriesArr: Array<any> = [
      {
        name: hasSource ? dataSet.source : "Consensus/VA actual",
        type: "column",
        data: dataObj.normal,
      },
    ];
    if (dataObj.yoy && dataObj.yoy.length) {
      seriesArr.push({
        name: "YOY Growth",
        type: "line",
        yAxis: 1,
        data: dataObj.yoy,
      });
    }
    const trendBarOptions: Highcharts.Options = {
      chart: {},

      xAxis: [
        {
          categories: dataSet.Value.x,
        },
      ],
      yAxis: yAxisArr,
      tooltip: {
        shared: true,
        useHTML: true,
        formatter: function () {
          const point = tooltipTrendData[this.points[0].key];
          const unitSymbol = tooltipTrendData.unit === "%" ? "bps" : "%";
          let isAmount = false;
          if (tooltipTrendData.unit === "Amount") {
            isAmount = true;
          }
          const hasSource = dataSet.source.toLowerCase() !== "consensus";
          let tooltipHtml = "";
          if (!hasSource) {
            tooltipHtml = `</span><span>${point.source} : `;
          } else {
            tooltipHtml = `</span><span>${dataSet.source} : `;
          }

          tooltipHtml +=
            isAmount === true
              ? tooltipTrendData.display_unit + point.consensus
              : point.consensus + tooltipTrendData.display_unit;
          tooltipHtml += "</span><br/>";
          const baseCurrency =
            dataSet.baseCurrency && dataSet.baseCurrFromMEData !== "Base"
              ? `(in ${dataSet.baseCurrency})`
              : "";
          if (dataObj && dataObj.yoy && dataObj.yoy.length) {
            tooltipHtml += `<span>YOY Growth ${baseCurrency} : ${
              point.yoy !== null ? `${point.yoy}${unitSymbol}` : "-"
            }</span>`;
          }
          return tooltipHtml;
        },
      },
      legend: {
        itemDistance: 50,
      },

      plotOptions: {
        series: {
          events: {
            legendItemClick: function () {
              return true;
            },
          },
        },
      },
      series: seriesArr,
    };
    this.seriesInfo = dataSet;
    let exportOptions: Highcharts.ExportingOptions = {};
    if (dataSet.export) {
      exportOptions = this.getExportSettings(dataSet, "trend-bar");
      trendBarOptions["exporting"] = exportOptions;
    }
    return trendBarOptions;
  }

  getRevisionChartOptions(
    dataSet,
    chartTypeDisplay: string,
    stockPriceData?,
    minInterval?,
  ) {
    this.chartTypeDisplay = chartTypeDisplay;
    const seriesObj = [];
    for (const obj in dataSet.data) {
      const sourceObj = {
        name: obj,
        data: [],
        type: dataSet.isValuationRatio ? "spline" : "line",
        fillColor: {
          linearGradient: [0, 0, 0, 300],
        },
        marker: {
          enabled: dataSet.data[obj].length === 1 ? true : false,
        },
        custom: {
          currencySymbol: dataSet.currency,
        },
      };

      const dataObj = this.formatRevisionData(
        dataSet.data[obj],
        dataSet.earnings_date,
      );
      sourceObj.data = dataObj;
      if (sourceObj.data.length === 0) {
        sourceObj.name = `${obj}(data unavailable)`;
      } else {
        seriesObj.push(sourceObj);
      }
    }
    if (stockPriceData) {
      const name = "PRICE";
      const stockPrice = stockPriceData.stockprice;
      const stockPriceLength = stockPrice.length;
      if (stockPriceLength) {
        const isLineIncreasing =
          stockPrice[stockPriceLength - 1][1] >= stockPrice[0][1];
        const sourceObj = {
          name: name,
          data: [],
          type: "spline",
          fillColor: {
            linearGradient: [0, 0, 0, 300],
          },
          color: isLineIncreasing ? "#00DB00" : "#FF1212",
          marker: {
            enabled: false,
          },
          yAxis: 1,
          custom: {
            currencySymbol: stockPriceData.currency,
          },
        };

        const dataObj = this.formatRevisionData(stockPriceData.stockprice);
        sourceObj.data = dataObj;
        seriesObj.push(sourceObj);
      }
    }
    return this.commonRevisionOptions(
      seriesObj,
      dataSet,
      stockPriceData,
      minInterval,
    );
  }

  getRevisionAnalysisChartOptions(dataSet, chartTypeDisplay: string) {
    this.chartTypeDisplay = chartTypeDisplay;
    const tooltipCurrentPreqData = {};
    const seriesObj = [];
    const blankObj = [];
    for (const obj in dataSet.data) {
      const sourceObj = {
        name: obj,
        data: [],
        type: "line",
        fillColor: {
          linearGradient: [0, 0, 0, 300],
        },
        marker: {
          enabled: true,
        },
      };
      const index = Object.keys(dataSet.data).indexOf(obj);
      tooltipCurrentPreqData[obj] = [];
      for (let i = 0; i < dataSet.data[obj].length; i++) {
        tooltipCurrentPreqData[obj][i] = {
          value: Highcharts.numberFormat(dataSet.data[obj][i][1], 2, ".", ","),
          date: dataSet.data[obj][i][2],
          pctVal: dataSet.data[obj][i][3],
        };
        if (index === 0) {
          blankObj.push({ value: " ", class: "blankSpace" });
        }
      }
      //seriesObj.push(sourceObj);
      const dataObj = this.formatRevisionData(dataSet.data[obj], "");
      sourceObj.data = dataObj;
      if (sourceObj.data.length === 0) {
        sourceObj.name = `${obj}(data unavailable)`;
      } else {
        seriesObj.push(sourceObj);
      }
    }
    const revAnalysisOptions: Highcharts.Options = {
      chart: {},
      xAxis: {
        gridLineWidth: 0,
        title: {
          text: "",
        },
        categories: dataSet.x_axis.slice(),
      },
      yAxis: {
        // Primary yAxis
        labels: {
          formatter: function () {
            return Highcharts.numberFormat(
              Number(this.value),
              dataSet.precision,
              ".",
              ",",
            );
          },
          style: {
            color: Highcharts.getOptions().colors[0],
          },
        },
        title: {
          text: "",
          style: {
            color: Highcharts.getOptions().colors[0],
          },
        },
      },
      tooltip: {
        shared: false,
        useHTML: true,
        formatter: function () {
          const point = tooltipCurrentPreqData[this.series.name];
          let isAmount = false;
          if (dataSet.unit === "Amount") {
            isAmount = true;
          }
          let tooltipHtml = "";
          tooltipHtml = "<span>Value: ";
          tooltipHtml +=
            isAmount === true
              ? dataSet.display_unit + point[this.point.index].value
              : point[this.point.index].value + dataSet.display_unit;
          tooltipHtml += "</span><br/>";
          tooltipHtml += `<span>Date: ${point[this.point.index].date}`;
          tooltipHtml += "</span><br/>";
          tooltipHtml += `<span>% Revision: ${point[this.point.index].pctVal}`;
          tooltipHtml += "</span>";
          return tooltipHtml;
        },
      },
      legend: {
        itemDistance: 50,
      },
      plotOptions: {
        series: {
          events: {
            legendItemClick: function () {
              return true;
            },
          },
        },
      },
      series: seriesObj,
    };
    this.seriesInfo = dataSet;
    let exportOptions: Highcharts.ExportingOptions = {};
    if (dataSet.export) {
      exportOptions = this.getExportSettings(dataSet, "revison-analysis");
      revAnalysisOptions["exporting"] = exportOptions;
    }

    return revAnalysisOptions;
  }

  getSurpriseAnalysisChartOptions(dataSet, chartTypeDisplay: string) {
    this.chartTypeDisplay = chartTypeDisplay;
    const surpriseObj = this.getSupriseFormattedData(dataSet);

    const supriseChartOption: Highcharts.Options = {
      chart: {
        type: "column",
        zoomType: "xy",
        panning: {
          enabled: true,
          type: "xy",
        },
        panKey: "shift",
      },
      xAxis: {
        gridLineWidth: 0,
        title: {
          text: "",
        },
        categories: dataSet.x.slice(),
      },
      yAxis: {
        // Primary yAxis
        labels: {
          formatter: function () {
            return `${Highcharts.numberFormat(
              Number(this.value),
              2,
              ".",
              ",",
            )}%`;
          },
          style: {
            color: Highcharts.getOptions().colors[0],
          },
        },
        title: {
          text: "% Surprise",
          style: {
            color: Highcharts.getOptions().colors[0],
          },
        },
      },
      tooltip: {
        shared: false,
        useHTML: true,
        formatter: function () {
          const point =
            surpriseObj.tooltipSurpriseData[this.key][this.series.name];
          let isAmount = false;
          if (dataSet.unit === "Amount") {
            isAmount = true;
          }

          let tooltipHtml = `<span>Earnings Date: ${point.date}</span><br/>`;
          tooltipHtml += `<span>${this.series.name} Expected: `;
          tooltipHtml +=
            isAmount === true
              ? dataSet.display_unit + point.expected
              : point.expected + dataSet.display_unit;
          tooltipHtml += "</span><br/>";
          tooltipHtml += "<span>Actual Value: ";
          tooltipHtml +=
            isAmount === true
              ? dataSet.display_unit + point.actual
              : point.actual + dataSet.display_unit;
          tooltipHtml += "</span><br/>";
          tooltipHtml += "<span>Surprise: ";
          tooltipHtml += `${
            point.surprise === -100 ? "-NM" : point.surprise
          } %`;
          tooltipHtml += "</span>";

          return tooltipHtml;
        },
      },
      plotOptions: {
        series: {
          dataLabels: {
            enabled: true,
            formatter: function () {
              let text = "";
              if (this.y > 0) {
                text = this.y > 100 ? "> 100%" : this.y.toString();
              } else {
                text = this.y < -100 ? "< -100%" : this.y.toString();
              }
              return text;
            },
          },
        },
      },
      legend: {
        itemDistance: 50,
        enabled: true,
      },

      series: surpriseObj.seriesArr,
    };
    this.seriesInfo = dataSet;
    let exportOptions: Highcharts.ExportingOptions = {};
    if (dataSet.export) {
      exportOptions = this.getExportSettings(dataSet, "surprise-analysis");
      supriseChartOption["exporting"] = exportOptions;
    }
    return supriseChartOption;
  }
  getSupriseFormattedData(dataSet) {
    const tooltipSurpriseColData = {};
    const seriesArr = [];

    for (const currentSource in dataSet.data) {
      const surprise = [];
      let nullCounter = 0;
      for (let i = 0; i < dataSet.data[currentSource].x.length; i++) {
        const currentVal = dataSet.data[currentSource].surprise_data[i];
        if (currentVal.length === 0) {
          surprise.push(null);
          nullCounter++;
          continue;
        }
        if (currentVal[1] > 100) surprise.push(105);
        else if (currentVal[1] < -100) surprise.push(-105);
        else if (
          (currentVal[3] === null || currentVal[3] === 0) &&
          parseInt(currentVal[2]) !== 0
        ) {
          surprise.push(null);
          nullCounter++;
        } else surprise.push(currentVal[1]);
        if (!tooltipSurpriseColData[dataSet.data[currentSource].x[i]])
          tooltipSurpriseColData[dataSet.data[currentSource].x[i]] = {};
        tooltipSurpriseColData[dataSet.data[currentSource].x[i]][
          currentSource
        ] = {
          date: currentVal[0],
          expected: Highcharts.numberFormat(currentVal[3], 2, ".", ","),
          actual: Highcharts.numberFormat(currentVal[2], 2, ".", ","),
          surprise: currentVal[1],
        };
      }
      const series = {
        name: currentSource,
        data: surprise,
      };
      if (dataSet.data[currentSource].x.length === nullCounter) {
        series.name = `${currentSource}(data unavailable)`;
      } else {
        seriesArr.push(series);
      }
    }
    return {
      seriesArr: seriesArr,
      tooltipSurpriseData: tooltipSurpriseColData,
    };
  }
  getSurpriseLineChartOptions(dataSet, chartTypeDisplay: string) {
    this.chartTypeDisplay = chartTypeDisplay;
    const surpriseObj = this.getSupriseFormattedData(dataSet);
    let isAmount = false;
    if (dataSet.unit === "Amount") {
      isAmount = true;
    }

    let titleText = dataSet.magnitude_description;
    if (isAmount) {
      titleText += `(${dataSet.currency})`;
    }
    const supriseLineOptions: Highcharts.Options = {
      chart: {},
      xAxis: {
        gridLineWidth: 0,
        title: {
          text: "",
        },
        categories: dataSet.x.slice(),
      },
      yAxis: {
        // Primary yAxis
        labels: {
          formatter: function () {
            return Highcharts.numberFormat(
              Number(this.value),
              dataSet.precision,
              ".",
              ",",
            );
          },
          style: {
            color: Highcharts.getOptions().colors[0],
          },
        },
        title: {
          text: titleText,
          style: {
            color: Highcharts.getOptions().colors[0],
          },
        },
      },
      tooltip: {
        shared: false,
        useHTML: true,
        formatter: function () {
          const unitSymbol = dataSet.unit === "%" ? "bps" : "%";
          let isAmount = false;
          if (dataSet.unit === "Amount") {
            isAmount = true;
          }

          let tooltipHtml = "";
          const point =
            surpriseObj.tooltipSurpriseData[this.key][this.series.name];
          if (!point) {
            return false;
          }
          if (this.series.name !== "Actual") {
            tooltipHtml = `<span>Earnings Date: ${point.date}</span><br/>`;
            tooltipHtml += `<span>${this.series.name} Expected: `;
            tooltipHtml +=
              isAmount === true
                ? dataSet.display_unit + point.surprise
                : point.surprise + dataSet.display_unit;
            tooltipHtml += "</span><br/>";
            tooltipHtml += "<span>Actual Value: ";
            tooltipHtml +=
              isAmount === true
                ? dataSet.display_unit + point.actual
                : point.actual + dataSet.display_unit;
            tooltipHtml += "</span><br/>";
            const surpriseVal = point.surprise;
            if (surpriseVal > 0)
              tooltipHtml += `<span>Surprise: ${surpriseVal}${unitSymbol}</span>`;
            else
              tooltipHtml += `<span>Surprise: (${surpriseVal}${unitSymbol})</span>`;
          } else {
            tooltipHtml = `<span>Date: ${point.date}</span><br/>`;
            tooltipHtml += "<span>Actual: ";
            tooltipHtml +=
              isAmount === true
                ? dataSet.display_unit + point.value
                : point.value + dataSet.display_unit;
            tooltipHtml += "</span>";
          }

          return tooltipHtml;
        },
      },
      legend: {
        itemDistance: 50,
        enabled: true,
      },
      series: surpriseObj.seriesArr,
    };
    this.seriesInfo = dataSet;
    let exportOptions: Highcharts.ExportingOptions = {};
    if (dataSet.export) {
      exportOptions = this.getExportSettings(dataSet, "surprise-line");
      supriseLineOptions["exporting"] = exportOptions;
    }
    return supriseLineOptions;
  }

  titleCase(str) {
    str = str.toLowerCase().split(" ");
    for (let i = 0; i < str.length; i++) {
      str[i] = str[i].charAt(0).toUpperCase() + str[i].slice(1);
    }
    return str.join(" ");
  }

  getExportSettings = function (dataSet, chartType) {
    const year = new Date().getFullYear();
    const copyrightText = `${year} Visible Alpha, L.L.C All Rights Reserved.`;
    const chartCategoryName = this.titleCase(chartType.split("-")[0]);
    const chartTypeName = this.chartTypeDisplay
      ? this.titleCase(this.chartTypeDisplay)
      : chartType.split("-")[1];
    const pdfPngOnly = chartCategoryName.toLowerCase() !== "revision";
    // add path condition
    const addinCharts = this.pageName === "addin-charts";
    let subTitleText = "";
    if (chartType === "surprise-analysis") {
      subTitleText = `${dataSet.parameter_name} : VA Actuals Vs Expected (Beat/Miss %)`;
    } else if (chartType === "surprise-line") {
      subTitleText = `${dataSet.parameter_name} : Actual vs Expectations`;
    } else {
      subTitleText = `${dataSet.parameter_name} - ${
        dataSet.period || dataSet.otherPeriod || ""
      }`;
    }
    const addinExportEvent = this.addinExportEvent;
    if (!dataSet.companyFullName && addinExportEvent) {
      dataSet.companyFullName = dataSet.ticker;
    }
    const exportOption: Highcharts.ExportingOptions = {
      enabled: true,
      allowHTML: true,
      menuItemDefinitions: {
        // Custom definition
        downloadXLSX: {
          onclick: function () {
            const div = document.createElement("div");
            let name = "",
              xlsxRows = [],
              rows = [];
            div.style.display = "none";
            document.body.appendChild(div);
            rows = this.getDataRows(true);

            const hSeries = this.series;
            xlsxRows = Highcharts.map(rows.slice(1), function (row, rowIndex) {
              return Highcharts.map(row, function (column, colIndex) {
                // Add currency to columns caption
                const currencySymbol =
                  rowIndex === 0 &&
                  hSeries[colIndex - 1] &&
                  hSeries[colIndex - 1].options["custom"] &&
                  hSeries[colIndex - 1].options["custom"]["currencySymbol"]
                    ? hSeries[colIndex - 1].options["custom"]["currencySymbol"]
                    : "";

                return {
                  type: typeof column === "number" ? "number" : "string",
                  value:
                    column === undefined
                      ? "-"
                      : `${column}${
                          currencySymbol ? ` (${currencySymbol})` : ""
                        }`,
                };
              });
            });

            const tableCap = [
              [{ type: "string", value: this.options.exporting.tableCaption }],
            ];
            xlsxRows = tableCap.concat(xlsxRows);
            name = this.options.exporting.filename || "chart";

            window.zipcelx({
              filename: name,
              sheet: {
                data: xlsxRows,
              },
            });
          },
          text: "Download XLSX",
        },
        addinExport: {
          text: "Export as Add-In formulas",
          onclick: () => {
            //add a callback to addin
            this.addinExportEvent.next(this.seriesInfo);
          },
        },
        addinPNGExport: {
          text: "Download PNG image",
          onclick: function () {
            //add a callback to addin
            const svgString = this.getSVG(this.options.exporting.chartOptions);
            const obj = {
              isPngExport: true,
              data: svgString,
            };
            addinExportEvent.next(obj);
          },
        },
      },
      chartOptions: {
        title: {
          text: `${chartCategoryName}|${chartTypeName}|${dataSet.companyFullName}`,
          align: "left",
          x: 10,
        },
        credits: {
          text: copyrightText,
          enabled: true,
          style: { fontSize: "8px" },
        },
        subtitle: {
          text: subTitleText,
          align: "left",
          x: 10,
        },
        legend: {
          itemMarginBottom: 10,
        },
      },
      filename: `${dataSet.ticker + this.titleCase(chartCategoryName)}Chart`,
      tableCaption: `${chartCategoryName}|${chartTypeName}|${dataSet.companyFullName}-${dataSet.parameter_name}${dataSet.period}`,
    };
    if (addinCharts) {
      exportOption["buttons"] = {
        contextButton: {
          menuItems: ["addinPNGExport", "addinExport"],
        },
      };
    } else if (pdfPngOnly) {
      exportOption["buttons"] = {
        contextButton: {
          menuItems: ["downloadPNG", "downloadPDF"],
        },
      };
    } else {
      exportOption["buttons"] = {
        contextButton: {
          menuItems: ["downloadPNG", "downloadPDF", "downloadXLSX"],
        },
      };
    }
    if (
      chartType.toLowerCase() === "revision-analysis" ||
      chartType.toLowerCase() === "revision-daily" ||
      chartType.toLowerCase() === "revision-monthly"
    ) {
      const chartRevLegend = {
        events: {
          load: (event) => {
            const left = event.target.renderer.width / 2 - 192 / 2;
            const top = event.target.renderer.height - 15;
            event.target.renderer
              .rect(left, top, 100, 40)
              .attr({ fill: "#e59700", width: "12px", height: "12px" })
              .add();
            event.target.renderer
              .text("Earnings", left + 15, top + 10, 100, 40)
              .add();
            event.target.renderer
              .rect(left + 85, top, 100, 40)
              .attr({ fill: "#7cb5ec", width: "12px", height: "12px" })
              .add();
            event.target.renderer
              .text("Revision Dates", left + 100, top + 10, 100, 40)
              .add();
          },
        },
      };
      exportOption.chartOptions["chart"] = chartRevLegend;
    }
    return exportOption;
  };
  getFormattedValue(chartData, value) {
    let isAmount = false;
    if (chartData.unit === "Amount") {
      isAmount = true;
    }

    value = Highcharts.numberFormat(value, chartData.precision, ".", ",");
    value = value < 0 ? `(${Math.abs(value)})` : value;
    const updatedVal =
      isAmount === true
        ? chartData.display_unit + value
        : value + chartData.display_unit;
    return updatedVal;
  }
  getFreshnessChartOptions(dataset, dataToUse, chartTypeDisplay: string) {
    this.chartTypeDisplay = chartTypeDisplay;
    const yourLabels = {},
      countLabels = {};
    const tickInterval =
      dataToUse.period === dataToUse.allPeriodsEnum["120_days"] ? 30 : 90;
    const tickIntervalInMs = tickInterval * 24 * 60 * 60 * 1000;
    let daysplit = 0;
    const otherAnalystSeries = [],
      youSeries = [];

    const lineSeries = dataset.filing_date.map((fDate) => {
      return {
        data: [
          [Date.parse(fDate.date), 0],
          [Date.parse(fDate.date), dataset.data.length + 1],
        ],
        type: "line",
        color: "rgba(0, 0, 0, .7)",
        dashStyle: "dash",
        showInLegend: false,
        marker: {
          symbol: "square",
        },
        name: "Filing ",
      };
    });
    dataset.data.forEach((analyst) => {
      yourLabels[analyst.analyst_id] = analyst.analyst;
      countLabels[analyst.analyst_id] = analyst.revision_count;
      analyst.values.forEach((value) => {
        if (analyst.analyst !== "You") {
          otherAnalystSeries.push({
            x: Date.parse(value.date),
            y: value.peer_id,
          });
        } else {
          youSeries.push({
            x: Date.parse(value.date),
            y: value.peer_id,
          });
        }
      });
    });
    const chartOption: Highcharts.Options = {
      title: {
        text: "",
      },
      subtitle: {
        text: '<span style="color:#000000;font-size:14px;font-weight:bold;">Model Revision Frequency</span>',
      },
      chart: {
        type: "scatter",
        zoomType: "xy",
      },
      xAxis: {
        type: "datetime",
        dateTimeLabelFormats: {
          month: "%e %b %Y",
          year: "%e %b %Y",
          day: "%e %b %Y",
          week: "%e %b %Y",
        },
        labels: {
          formatter: function () {
            const monthNames = [
              "Jan",
              "Feb",
              "Mar",
              "Apr",
              "May",
              "Jun",
              "Jul",
              "Aug",
              "Sep",
              "Oct",
              "Nov",
              "Dec",
            ];
            let s = "";
            const d = new Date(this.value);
            if (dataToUse.period === dataToUse.allPeriodsEnum["120_days"]) {
              //Format the x-axis date values as per spec
              if (this.isFirst === true) {
                daysplit = 0;
                s = `${daysplit}d<br/>${d.getDate()} ${
                  monthNames[d.getMonth()]
                } ${d.getFullYear()}`;
              } else {
                daysplit = daysplit + 30;
                s = `-${daysplit}d<br/>${d.getDate()} ${
                  monthNames[d.getMonth()]
                } ${d.getFullYear()}`;
              }
            } else {
              if (this.isFirst === true) {
                daysplit = 0;
                s = `${daysplit}d<br/>${d.getDate()} ${
                  monthNames[d.getMonth()]
                } ${d.getFullYear()}`;
              } else {
                daysplit = daysplit + 3;
                s = `-${daysplit}m<br/>${d.getDate()} ${
                  monthNames[d.getMonth()]
                } ${d.getFullYear()}`;
              }
            }

            return s;
          },
        },
        tickPositioner: function () {
          let act = this.max;
          const ticks = [];
          while (act > this.min) {
            ticks.push(act);
            act = act - tickIntervalInMs;
          }
          return ticks;
        },
        max: new Date().getTime(), //set right most point, and calculate periods backwards for 120d and 12m
      },
      yAxis: [
        {
          title: {
            text: "",
          },
          tickInterval: 1,
          startOnTick: false,
          min: 0,
          labels: {
            formatter: function () {
              const value = yourLabels[this.value];
              return value !== "undefined" ? value : this.value;
            },
          },
        },
        {
          linkedTo: 0,
          title: {
            text: "",
          },
          opposite: true,
          labels: {
            formatter: function () {
              if (this.value === 1) {
                //To change the color of total revisons of 'YOU' on the right y-axis
                return `<span style="border:1px solid blue;fill:#0078FF;">${
                  countLabels[this.value]
                }</span>`;
              }
              return countLabels[this.value];
            },
          },
        },
      ],
      legend: {
        layout: "horizontal",
        align: "center",
        verticalAlign: "bottom",
      },
      plotOptions: {
        series: {
          //To disable the popover as soon as mouse is moved away from series,
          //otherwise popover is not removed until mouse moves to another series
          stickyTracking: false,
        },
        bubble: {
          maxSize: "2px",
        },
        scatter: {
          marker: {
            radius: 1,
            states: {
              hover: {
                enabled: true,
                lineColor: "rgb(100,100,100)",
              },
            },
          },
        },
      },
      tooltip: {
        formatter: function () {
          const tooltipHtml = `
            <b> ${this.series.name} </b> <br />
            ${Highcharts.dateFormat("%e %b %Y", this.point.x)}
          `;
          return tooltipHtml;
        },
      },
      exporting: {
        enabled: false,
      },
      series: [
        {
          name: "Peers",
          type: "bubble",
          color: "rgba(0, 224, 111, .6)",
          dataLabels: {
            align: "right",
            format: "({point.count})",
          },
          data: otherAnalystSeries,
        },
        {
          name: "You",
          type: "bubble",
          color: "rgba(0, 174, 239, .6)",
          data: youSeries,
        },
        ...lineSeries,
      ],
    };
    return chartOption;
  }

  getForecastChartOptions(dataSet, chartTypeDisplay: string) {
    this.chartTypeDisplay = chartTypeDisplay;
    const quarterliesData = dataSet.data[0];
    const annualsData = dataSet.data[1];

    const finalQuarterlies = quarterliesData.values.map((element, key) => {
      if (key === quarterliesData.values.length - 1)
        return {
          y: element,
          color: "#00aeef",
        };
      return {
        y: element,
        color: "#00e06f",
      };
    });
    const finalAnnuals = annualsData.values.map((element, key) => {
      if (key === annualsData.values.length - 1)
        return {
          y: element,
          color: "#7fd6f7",
        };
      return {
        y: element,
        color: "#87ffc2",
      };
    });

    const chartOption: Highcharts.Options = {
      chart: {
        type: "bar",
      },
      title: {
        text: "",
      },
      exporting: {
        enabled: false,
      },
      xAxis: {
        categories: dataSet.peer_list,
        title: {
          text: "Peers",
          textAlign: "left",
          rotation: 0,
          x: 0,
          y: 0,
        },
        tickWidth: 0,
      },
      yAxis: [
        {
          min: 0.5,
          gridLineColor: "transparent",
          title: {
            text: "",
          },
          categories: dataSet.year_lists,
          minPadding: 0,
          startOnTick: false, // allow to start not from tick
          endOnTick: false,
          labels: {
            format: "{value}",
          }, // allow to end not at tick
        },
      ],
      tooltip: {
        headerFormat: "<b>{series.name}</b><br>",
      },
      legend: {
        reversed: true,
      },
      plotOptions: {
        series: {
          stacking: "normal",
          states: {
            inactive: {
              opacity: 1,
            },
          },
        },
        bar: {
          pointPadding: 0.8,
          groupPadding: 1,
          events: {
            legendItemClick: function () {
              return false;
            },
          },
        },
      },
      series: [
        {
          name: annualsData.name,
          data: finalAnnuals,
          color: "#87ffc2",
          type: "bar",
        },
        {
          name: quarterliesData.name,
          data: finalQuarterlies,
          color: "#00e06f",
          type: "bar",
        },
      ],
    };
    return chartOption;
  }
  setPageName(page) {
    this.pageName = page;
  }
}
